// TableSettings.js
import React, { useState, useContext, useEffect } from "react";
import TableSettingModal from "./TableSettingModal";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import "./styles/PropertiesComponent.css";
import CommonButton from "../elments/CommonButton";
import Loader from "./Loader";
import VariableContext from "../contexts/VariableContext";
import ConvertTypeModal from "./ConvertTypeModal";
import QuestionJsonModal from "./QuestionJsonModal";
import { duplicateVariable, SPECIAL_ROWS, FAMILIES, DIMS, COMPUTED_TYPES, typeName } from "../utils/conversions";
import { v4 as uuidv4 } from "uuid";

const DEFAULT_CHART_CONFIG = {
  enabled: true,
  chartType: "doughnut",       // doughnut | bar | stackedbar
  bannerScope: "main",         // main | targets
  bannerName: null,
  sigDisplay: "none",          // boxes | symbols | none
  weighted: false,
  rowScope: "valuesOnly",      // valuesOnly | valuesAndNets
  slideGroups: [],             // [{ id, label, pointLetters: [] }] — only used when bannerScope === "targets"
  sigHeaderFilter: "",         // ';'-separated banner headerText values — only used when bannerScope === "main".
                                // Restricts sig/compliment callouts to banner points under these headers; blank = all.
  netsInChart: false,          // bar/doughnut only. false (default) = nets shown separately (sidecar textbox
                                // for bar, center-hole text for doughnut). true = nets plotted inside the chart.
  stackedOrientation: "horizontal", // stackedbar only. horizontal (default, unchanged) | vertical
  excludedRowIndices: [],      // stackedbar only. row/value indices left out of the chart's series
  stubColors: "",               // this chart's own ';'-separated hex colors (plain, positional, and/or
                                 // 'Stub Name: #color' overrides) — independent per question, unlike
                                 // the deck-level Global PPT Color
  autoShades: false,            // this chart's own "generate lighter shades of the global color" toggle
  noGlobalColor: false,         // this chart's own opt-out of the deck's Global PPT Color — when true,
                                 // this chart ignores it (and skips auto-shades, which derive from it)
                                 // while its own Stub Colors and the default palette still apply
  sortEnabled: false,           // this chart's own stub/statement sorting toggle
  sortMode: "normal",           // normal (% desc, Other/None of these/etc. always last) |
                                 // netsAsNet (Nets sorted by their own %, statements sorted within each Net)
  sortExcludeStubs: "",         // ';'-separated stub names that skip % sorting and always end up last, in
                                 // the order typed here — unioned with a hardcoded default trailing set
                                 // (Other / None of these / Don't know / Can't remember)
};

// New synthetic rows/values/nets (created in the UI rather than coming from the
// SPSS file) get an auto-assigned index starting here and counting down — keeps
// them from ever colliding with real SPSS-derived indices, which are positive.
const NEW_ROW_INDEX_START = -5555;
const getNextSyntheticIndex = (items) => {
  const syntheticIndices = (items || [])
    .map((it) => Number(it?.index))
    .filter((n) => !isNaN(n) && n <= NEW_ROW_INDEX_START);
  return syntheticIndices.length ? Math.min(...syntheticIndices) - 1 : NEW_ROW_INDEX_START;
};

// ── Property → question-type visibility map (from "Properties for Groups ERAAI.xlsx") ──
// Keys not listed here are shown for every type. Custom/Stacked/Volume/Share always show
// every property regardless of this map (per the sheet's "show all properties above" note).
const NON_MULTIPLE_TYPES = [
  "SingleResponse", "Single2DGrid", "Single3DGrid", "Single4DGrid",
  "Numeric", "Numeric2DGrid", "Numeric3DGrid", "Numeric4DGrid",
];
const GRID_EXCEPT_MULTIPLE2D_TYPES = [
  "Single2DGrid", "Single3DGrid", "Single4DGrid",
  "Numeric2DGrid", "Numeric3DGrid", "Numeric4DGrid",
  "Multiple3DGrid", "Multiple4DGrid",
];
const PROPERTY_TYPE_VISIBILITY = {
  median: NON_MULTIPLE_TYPES,
  SE: NON_MULTIPLE_TYPES,
  STD: NON_MULTIPLE_TYPES,
  allstats: NON_MULTIPLE_TYPES,
  meansummary: GRID_EXCEPT_MULTIPLE2D_TYPES,
  grid: GRID_EXCEPT_MULTIPLE2D_TYPES,
  individualbase: GRID_EXCEPT_MULTIPLE2D_TYPES,
  reverseGrid: GRID_EXCEPT_MULTIPLE2D_TYPES,
};
const COMPUTED_TYPES_SHOW_ALL_PROPERTIES = ["Custom", "Stacked", "Volume", "Share"];

// All selectable question types for the "Type" dropdown: family x dims, plus computed types.
const ALL_QUESTION_TYPES = [
  ...FAMILIES.flatMap((family) => DIMS.map((dims) => typeName(family, dims)).filter(Boolean)),
  ...COMPUTED_TYPES,
];

const isPropertyVisibleForType = (property, type) => {
  if (COMPUTED_TYPES_SHOW_ALL_PROPERTIES.includes(type)) return true;
  const allowed = PROPERTY_TYPE_VISIBILITY[property];
  return !allowed || allowed.includes(type);
};

const PropertiesComponent = ({
  selectedVariable,
  setdataGroup,
  dataGroup,
  selectedKey,
  setSelectedVariable,
  projectId,nets, setNets,
  banners, chartDecks, onChangeChartDecks,
  allowedExports,
}) => {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const { selectedProject, selectedClient, powner } = useContext(VariableContext);
  const [activeTab, setActiveTab] = useState("properties");
  const [netLabel, setNetLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);
  // Single-question tab-script preview (Wincross/Uncle/Quantum) — read-only,
  // mirrors Survey Builder's DC/DM buttons for Decipher/Dimension.
  const [scriptPreviewOpen, setScriptPreviewOpen] = useState(false);
  const [scriptPreviewTitle, setScriptPreviewTitle] = useState("");
  const [scriptPreviewData, setScriptPreviewData] = useState(null); // string | { axes, banner, questionFiles }
  const [scriptPreviewLoading, setScriptPreviewLoading] = useState(false);
  // "Combine Variables" tab state — pick SingleResponse/Multiple2DGrid questions to merge
  const [combineSelectedKeys, setCombineSelectedKeys] = useState([]);
  const [combineTitle, setCombineTitle] = useState("");
  // One-step undo for type conversions: snapshot of dataGroup before the last apply.
  const [convSnapshot, setConvSnapshot] = useState(null);
  // "Add to Chart" section state
  const [chartDeckTarget, setChartDeckTarget] = useState(""); // deck name to add this question's chart to
  const [chartAddStatus, setChartAddStatus] = useState(null); // null | "saving" | "done"
  // Global PPT Color is deck-level, not chart-level: it's shared by every chart in the
  // target deck (old and new — the backend re-applies it at export time from the deck
  // record, see chartExport.build_deck_pptx), so it's tracked per selected target deck
  // here rather than inside chartConfig. Stub Colors / "Generate Lighter Shades" ARE
  // chart-specific and live in chartConfig itself (DEFAULT_CHART_CONFIG.stubColors /
  // .autoShades) so each question keeps its own independent values.
  const [globalPptColor, setGlobalPptColor] = useState("");
  // Keep the Global PPT Color field in sync with whichever deck is currently targeted —
  // switching to a deck that already has a saved global color should show that color,
  // not whatever was last typed for a different deck.
  useEffect(() => {
    const deckName = (chartDeckTarget || "").trim();
    const deck = (chartDecks || []).find((d) => d.name === deckName);
    if (deck) setGlobalPptColor(deck.globalColor || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartDeckTarget]);

  // All possible boolean properties with default values.
  // Note: netswithingroups is handled in the dedicated Ranking section below — not listed here.
  const allBooleanProperties = {
    removeTab: false,
    mean: false,
    median: false,
    removeIndividual: false,
    summaryNet: false,
    grid: false,
    removerows: false,
    szr: false,
    reverseGrid: false,
    // addNets: false,         // commented out for now — see PropertiesComponent type map
    // saveToExcel: false,     // commented out for now — see PropertiesComponent type map
    // downloadDVExcel: false, // commented out for now — see PropertiesComponent type map
    // SPSSSyntax: false,      // commented out for now — see PropertiesComponent type map
    // conceptTables: false,   // commented out for now — see PropertiesComponent type map
    // reverseLocation: false, // commented out for now — see PropertiesComponent type map
    // dataCuts: false,        // commented out for now — see PropertiesComponent type map
    SE: false,
    STD: false,
    allstats: false,
    showNetswithinLabels: false,
    baseThoseAnswering: false,
    individualbase: false,
    meansummary: false,
  };

  const TOGGLE_LABELS = {
    removeTab:            "Remove Tab",
    mean:                 "Mean",
    median:               "Median",
    removeIndividual:     "Remove Individual",
    summaryNet:           "Summary Net",
    grid:                 "Grid",
    removerows:           "Remove Rows",
    szr:                  "SZR",
    reverseGrid:          "Reverse Grid",
    // addNets:              "Add Nets",
    // saveToExcel:          "Save to Excel",
    // downloadDVExcel:      "DV Excel",
    // SPSSSyntax:           "SPSS Syntax",
    // conceptTables:        "Concept Tables",
    // reverseLocation:      "Reverse Location",
    // dataCuts:             "Data Cuts",
    SE:                   "Std Error",
    STD:                  "Std Dev",
    allstats:             "All Stats",
    showNetswithinLabels: "Nets in Labels",
    baseThoseAnswering:   "Base: Those Answering",
    individualbase:       "Individual Base",
    meansummary:          "Mean Summary",
  };

  // All possible text properties with default values
  const allTextProperties = {
    combo: "",
    flttb: "",
    stacked: "",
    nets: "",
    type: selectedVariable?.type || "",
    table: selectedVariable?.table || "freq",
    Qtitle: selectedVariable?.Qtitle || "",
    basetitle: selectedVariable?.basetitle || "All respondents",
    tabSpectitle: selectedVariable?.tabSpectitle || selectedVariable?.Qtitle || "",
    Logic: selectedVariable?.Logic || "",
    weightVar: selectedVariable?.weightVar || ""
    
  };

  if (!selectedVariable) {
    return (
      <div className="variable-properties">
        <h2>No variable selected</h2>
        <p>Please select a variable from the list to view its properties.</p>
      </div>
    );
  }

  // Helper to get property value (exists? use it, otherwise use default).
  // Also handles known case-variant aliases so old data with different casing is
  // still surfaced by the correct canonical key (e.g. removetab → removeTab).
  const PROPERTY_ALIASES = { removeTab: "removetab" };
  const getPropertyValue = (property, defaultValue = false) => {
    if (selectedVariable.hasOwnProperty(property)) return selectedVariable[property];
    const alias = PROPERTY_ALIASES[property];
    if (alias && selectedVariable.hasOwnProperty(alias)) return selectedVariable[alias];
    return defaultValue;
  };

  // Helper to set property. When a canonical property has a known alias, the alias
  // key is removed so the variable never holds both forms simultaneously.
  const setPropertyValue = (property, value) => {
    setSelectedVariable((prev) => {
      const next = { ...prev, [property]: value };
      const alias = PROPERTY_ALIASES[property];
      if (alias) delete next[alias];
      return next;
    });
  };

  // ── "Add to Chart" (chartConfig) helpers ──────────────────────────────────
  const chartConfig = { ...DEFAULT_CHART_CONFIG, ...(getPropertyValue("chartConfig", {}) || {}) };
  const updateChartConfig = (patch) => {
    const next = { ...chartConfig, ...patch };
    setPropertyValue("chartConfig", next);
    // Also write straight into dataGroup, not just the ephemeral selectedVariable snapshot.
    // selectedVariable is re-derived fresh from dataGroup on every question click (see
    // FreqProvider's title-click handler) and is otherwise only synced back into dataGroup
    // when switching titles — so without this, stub colors/auto-shades typed here would
    // vanish the moment something re-renders before that switch-time sync fires (e.g. right
    // after exporting a PPT, or navigating to another chart and back).
    if (selectedKey) {
      setdataGroup && setdataGroup((prev) => ({
        ...prev,
        [selectedKey]: { ...(prev?.[selectedKey] || {}), chartConfig: next },
      }));
    }
  };

  const commitDeckGlobalColor = (color) => {
    const deckName = (chartDeckTarget || "").trim();
    if (!deckName) return;
    const existingDecks = chartDecks || [];
    const deckIdx = existingDecks.findIndex((d) => d.name === deckName);
    const trimmedColor = color.trim() || null;
    // Upsert: a color typed before the deck's first chart is added would otherwise be
    // silently dropped (nothing to attach it to) and disappear on the next render.
    const nextDecks = deckIdx === -1
      ? [...existingDecks, { name: deckName, slides: [], enabled: true, globalColor: trimmedColor }]
      : existingDecks.map((d, i) => (i === deckIdx ? { ...d, globalColor: trimmedColor } : d));
    onChangeChartDecks && onChangeChartDecks(nextDecks);
  };

  const chartBanner = (banners || []).find((b) => b.name === chartConfig.bannerName) || null;
  const chartBannerPoints = (chartBanner?.rows || []).map((r, i) => ({
    letter: r.letter || String.fromCharCode(65 + i),
    text: r.text || r.letter || `Point ${i + 1}`,
  }));

  // Rows/values that can be excluded from a stacked chart's series — for
  // Single2DGrid/Numeric2DGrid this is the rating scale, for Multiple2DGrid
  // it's the brands, otherwise the question's answer options.
  const excludableRows = (
    selectedVariable.type === 'Multiple2DGrid'
      ? selectedVariable.Rows
      : selectedVariable.values
  ) || [];
  const toggleExcludedRow = (idx) => {
    const key = String(idx);
    const current = chartConfig.excludedRowIndices || [];
    const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    updateChartConfig({ excludedRowIndices: next });
  };

  const addSlideGroup = () => {
    const groups = chartConfig.slideGroups || [];
    updateChartConfig({
      slideGroups: [...groups, { id: uuidv4(), label: `Slide ${groups.length + 1}`, pointLetters: [] }],
    });
  };
  const removeSlideGroup = (groupId) => {
    updateChartConfig({ slideGroups: (chartConfig.slideGroups || []).filter((g) => g.id !== groupId) });
  };
  const updateSlideGroup = (groupId, patch) => {
    updateChartConfig({
      slideGroups: (chartConfig.slideGroups || []).map((g) => (g.id === groupId ? { ...g, ...patch } : g)),
    });
  };
  const toggleSlideGroupPoint = (groupId, letter) => {
    const group = (chartConfig.slideGroups || []).find((g) => g.id === groupId);
    if (!group) return;
    const has = (group.pointLetters || []).includes(letter);
    const nextLetters = has
      ? group.pointLetters.filter((l) => l !== letter)
      : [...(group.pointLetters || []), letter];
    updateSlideGroup(groupId, { pointLetters: nextLetters });
  };

  const addChartToDeck = () => {
    const deckName = (chartDeckTarget || "").trim();
    if (!deckName || !selectedVariable || !selectedKey) return;
    setChartAddStatus("saving");
    // stubColors/autoShades are already part of chartConfig (chart-specific); globalColor
    // is deck-level and gets written onto the deck object itself, below.
    const entry = {
      id: uuidv4(),
      datakey: selectedKey,
      chartConfig: { ...chartConfig },
      qtitleSnapshot: selectedVariable.Qtitle || selectedVariable.Question_number || selectedKey,
      addedAt: new Date().toISOString(),
    };
    const existingDecks = chartDecks || [];
    const deckIdx = existingDecks.findIndex((d) => d.name === deckName);
    const deckGlobalColor = globalPptColor.trim() || null;
    let nextDecks;
    if (deckIdx === -1) {
      nextDecks = [...existingDecks, { name: deckName, slides: [entry], enabled: true, globalColor: deckGlobalColor }];
    } else {
      nextDecks = existingDecks.map((d, i) =>
        i === deckIdx ? { ...d, slides: [...(d.slides || []), entry], globalColor: deckGlobalColor } : d
      );
    }
    onChangeChartDecks && onChangeChartDecks(nextDecks);
    setChartAddStatus("done");
    setTimeout(() => setChartAddStatus(null), 1800);
  };

  const handleSaveCustomVariable = async () => {
    const isCustomType = selectedVariable?.type === 'Custom';
    const isMergeType  = !isCustomType && selectedVariable?.type === 'Multiple2DGrid';
    const isSingleGridType = !isCustomType && selectedVariable?.type === 'Single2DGrid';
    const hasCustomRows = (selectedVariable?.customRows?.length || 0) > 0;
    if (!selectedVariable || (!isCustomType && !hasCustomRows)) return;

    const customRows = selectedVariable.customRows || [];
    const allRowsWithLogic = customRows.filter(r => r.logic && r.logic.trim());
    // Row Calc rows (R1+R2 style) reference other rows' already-tabulated
    // freq/percent — there's no per-respondent value to save as a variable,
    // so they're excluded here and left untouched in customRows.
    const rowCalcRows = allRowsWithLogic.filter(r => r.rowCalc && !r.formula);
    // Formula rows (Mean/Sum/Median over raw variables) go to the new
    // Numeric/Numeric2DGrid path; plain logic rows keep the original
    // Multiple2DGrid / grid-values path unchanged.
    const formulaRows = allRowsWithLogic.filter(r => r.formula);
    const rowsWithLogic = allRowsWithLogic.filter(r => !r.rowCalc && !r.formula);

    if (rowCalcRows.length > 0) {
      toast.warning(
        `Row Calc row(s) [${rowCalcRows.map(r => r.label || r.index).join(', ')}] can't be saved as a ` +
        `variable — they reference other rows' tabulated values, not per-respondent data. Skipped.`
      );
    }

    if (rowsWithLogic.length === 0 && formulaRows.length === 0) {
      toast.warning("No custom rows with logic or formula defined. Please add some first.");
      return;
    }

    // For merge into existing Multiple2DGrid: block on duplicate indices before confirming
    if (isMergeType && rowsWithLogic.length > 0) {
      const existingIndices = new Set((selectedVariable.Rows || []).map(r => String(r.index)));
      const duplicates = rowsWithLogic.filter(cr => existingIndices.has(String(cr.index)));
      if (duplicates.length > 0) {
        toast.error(
          `Duplicate row index(es) found: ${duplicates.map(d => d.index).join(', ')}. ` +
          `Please change the custom row indices before saving.`
        );
        return;
      }
    }

    // For Single2DGrid: the custom row index becomes a value code inside the
    // grid data, so block indices that already exist as answer codes
    if (isSingleGridType && rowsWithLogic.length > 0) {
      const existingCodes = new Set(
        (selectedVariable.values || []).map(v => String(parseInt(v.index)))
      );
      const duplicates = rowsWithLogic.filter(cr => existingCodes.has(String(parseInt(cr.index))));
      if (duplicates.length > 0) {
        toast.error(
          `Value code(s) already used by this grid: ${duplicates.map(d => d.index).join(', ')}. ` +
          `Please change the custom row indices before saving.`
        );
        return;
      }
    }

    if (isSingleGridType && formulaRows.length > 0) {
      toast.warning(
        `Formula row(s) can't be written as grid value codes — they'll be saved separately as a ` +
        `numeric variable instead of into "${selectedKey}"'s grid data.`
      );
    }

    const messageParts = [];
    if (rowsWithLogic.length > 0) {
      messageParts.push(
        isCustomType
          ? `${rowsWithLogic.length} binary (Yes/No) variable(s), converting this question to Multiple2DGrid`
          : isMergeType
            ? `${rowsWithLogic.length} new binary row(s) added to "${selectedKey}" (Multiple2DGrid)`
            : isSingleGridType
              ? `code(s) ${rowsWithLogic.map(r => r.index).join(', ')} written into "${selectedKey}"'s grid data (existing answers of qualified respondents will be REPLACED)`
              : `a new Multiple2DGrid "${selectedKey}new" with ${rowsWithLogic.length} binary (Yes/No) variable(s)`
      );
    }
    if (formulaRows.length > 0) {
      const numType = formulaRows.length === 1 ? "Numeric" : "Numeric2DGrid";
      messageParts.push(
        `${formulaRows.length} numeric variable(s) computed from the Formula row(s), saved as a new ` +
        `${numType} question "${selectedKey}num" (allstats on by default)`
      );
    }

    const confirmed = await confirm({
      title: "Save Custom Variable",
      message: `This will create: ${messageParts.join('; and ')}. Rows without logic/formula are skipped. Proceed?`,
      confirmText: "Save Variable",
    });
    if (!confirmed) return;

    // Rows consumed by this save (logic + formula) come out of customRows;
    // Row Calc rows are untouched since they were never sent anywhere.
    const remainingCustomRows = rowCalcRows;
    const { customRows: _removed, ...baseVariableRaw } = selectedVariable;
    const baseVariable = remainingCustomRows.length > 0
      ? { ...baseVariableRaw, customRows: remainingCustomRows }
      : baseVariableRaw;

    setLoading(true);
    // Each step below persists via /save_all_data_list, which REPLACES the
    // stored dataGroup wholesale — so every payload must be built from this
    // running total, not the (possibly now-stale) `dataGroup` prop, or an
    // earlier step's save would get wiped out by a later one.
    let workingDataGroup = { ...dataGroup };
    try {
      // ── Formula rows -> new Numeric / Numeric2DGrid sibling question ──────
      if (formulaRows.length > 0) {
        const { data: numData } = await apiService.post('/save-custom-as-numericgrid', {
          projectId,
          key: selectedKey,
          customRows: formulaRows,
          dataGroup: workingDataGroup,
          client: JSON.stringify(selectedClient || {}),
          project: JSON.stringify(selectedProject || {}),
          user: powner || '',
        });

        if (!numData.success) {
          toast.error("Formula save error: " + (numData.error || "Unknown error"));
        } else {
          const numKey = selectedKey + "num";
          const isSingleNumeric = numData.varNames.length === 1;
          const numRows = numData.varNames.map(v => {
            const src = formulaRows.find(r => String(r.index) === String(v.row));
            return { index: v.row, title: src?.label || `Row ${v.row}` };
          });
          const numVariable = isSingleNumeric
            ? {
                type: "Numeric",
                Qtitle: numRows[0]?.title || numKey,
                basetitle: selectedVariable.basetitle || "All respondents",
                Logic: selectedVariable.Logic || "",
                allstats: true,
                varName: [{ Name: numData.varNames[0].Name }],
                spsssyntax: numData.spsssyntax || "",
              }
            : {
                type: "Numeric2DGrid",
                Qtitle: `${selectedVariable.Qtitle || selectedKey} (Computed)`,
                basetitle: selectedVariable.basetitle || "All respondents",
                Logic: selectedVariable.Logic || "",
                allstats: true,
                Rows: numRows,
                varName: numData.varNames.map(v => ({ Name: v.Name, row: v.row })),
                spsssyntax: numData.spsssyntax || "",
              };

          workingDataGroup = { ...workingDataGroup, [numKey]: numVariable, [selectedKey]: baseVariable };
          setdataGroup((prev) => ({ ...prev, [numKey]: numVariable, [selectedKey]: baseVariable }));
          await apiService.post("/save_all_data_list", { projectId, dataGroup: workingDataGroup });

          toast.success(`Created "${numKey}" (${numVariable.type}) with ${numData.varNames.length} computed variable(s).`);
          if (numData.errors && numData.errors.length > 0) {
            toast.warning(`${numData.errors.length} Formula row(s) had errors and were skipped.`);
          }
        }
      }

      // ── Plain logic rows -> existing Multiple2DGrid / grid-values flow ────
      if (rowsWithLogic.length === 0) {
        setSelectedVariable(baseVariable);
        if (formulaRows.length === 0) {
          // Only Row Calc rows were present — nothing was saved above yet.
          workingDataGroup = { ...workingDataGroup, [selectedKey]: baseVariable };
          setdataGroup((prev) => ({ ...prev, [selectedKey]: baseVariable }));
          await apiService.post("/save_all_data_list", { projectId, dataGroup: workingDataGroup });
        }
        return;
      }

      if (isSingleGridType) {
        // Single2DGrid: write each custom row's index as a value code into every
        // grid variable (based on its condition) and append the value label —
        // the variable stays a Single2DGrid, no new SPSS variables are created.
        const { data } = await apiService.post('/save-single2dgrid-custom-values', {
          projectId,
          key: selectedKey,
          customRows: rowsWithLogic,
          varName: selectedVariable.varName || [],
          dataGroup: workingDataGroup,
          client: JSON.stringify(selectedClient || {}),
          project: JSON.stringify(selectedProject || {}),
          user: powner || '',
        });

        if (!data.success) {
          toast.error("Error: " + (data.error || "Unknown error"));
          return;
        }

        const appliedRows = data.applied || [];

        // Match the key/index style already used by this variable ("99.0" vs "99")
        const usesDotZero = Object.keys(baseVariable.valueLabels || {})
          .some(k => String(k).endsWith('.0'));
        const newValues = [...(baseVariable.values || [])];
        const newValueLabels = { ...(baseVariable.valueLabels || {}) };
        appliedRows.forEach(r => {
          newValues.push({ index: usesDotZero ? `${r.index}.0` : r.index, title: r.label });
          newValueLabels[usesDotZero ? `${r.index}.0` : `${r.index}`] = r.label;
        });

        const updatedVariable = {
          ...baseVariable,
          values: newValues,
          valueLabels: newValueLabels,
        };
        setSelectedVariable(updatedVariable);
        workingDataGroup = { ...workingDataGroup, [selectedKey]: updatedVariable };
        setdataGroup((prev) => ({ ...prev, [selectedKey]: updatedVariable }));
        await apiService.post("/save_all_data_list", { projectId, dataGroup: workingDataGroup });

        toast.success(`Variable saved! ${appliedRows.length} value code(s) written into the grid data.`);
        if (data.errors && data.errors.length > 0) {
          toast.warning(`${data.errors.length} row(s) had errors and were skipped.`);
        }
        return;
      }

      const { data } = await apiService.post('/save-custom-as-multiple2dgrid', {
        projectId,
        key: selectedKey,
        customRows: rowsWithLogic,
        dataGroup: workingDataGroup,
        client: JSON.stringify(selectedClient || {}),
        project: JSON.stringify(selectedProject || {}),
        user: powner || '',
      });

      if (!data.success) {
        toast.error("Error: " + (data.error || "Unknown error"));
        return;
      }

      const newRows = rowsWithLogic.map(cr => {
        const row = { index: cr.index, title: cr.label };
        if (cr.logicBase && cr.logicBase.trim()) row.Logic = cr.logicBase.trim();
        if (cr.norank) row.norank = true;
        if (cr.fix) row.fix = true;
        return row;
      });
      const newValues = [{ index: 0, title: "No" }, { index: 1, title: "Yes" }];
      const newValueLabels = { "0.0": "No", "1.0": "Yes" };

      // Append the newly generated SPSS syntax to whatever was already saved here from
      // an earlier "Save Custom Variable" pass, so the copy/paste box accumulates history.
      const mergedSpssSyntax = [baseVariable.spsssyntax, data.spsssyntax].filter(Boolean).join("\n");

      if (isCustomType) {
        // Convert the variable itself in-place to Multiple2DGrid
        const updatedVariable = {
          ...baseVariable,
          type: "Multiple2DGrid",
          Rows: newRows,
          values: newValues,
          valueLabels: newValueLabels,
          varName: data.varNames,
          spsssyntax: mergedSpssSyntax,
        };
        setSelectedVariable(updatedVariable);
        workingDataGroup = { ...workingDataGroup, [selectedKey]: updatedVariable };
        setdataGroup((prev) => ({ ...prev, [selectedKey]: updatedVariable }));
        await apiService.post("/save_all_data_list", { projectId, dataGroup: workingDataGroup });
      } else if (isMergeType) {
        // Merge new rows into the existing Multiple2DGrid variable
        const updatedVariable = {
          ...baseVariable,
          Rows:    [...(baseVariable.Rows    || []), ...newRows],
          varName: [...(baseVariable.varName || []), ...data.varNames],
          spsssyntax: mergedSpssSyntax,
        };
        setSelectedVariable(updatedVariable);
        workingDataGroup = { ...workingDataGroup, [selectedKey]: updatedVariable };
        setdataGroup((prev) => ({ ...prev, [selectedKey]: updatedVariable }));
        await apiService.post("/save_all_data_list", { projectId, dataGroup: workingDataGroup });
      } else {
        // Keep the original variable (without the processed customRows), add a new Multiple2DGrid variable
        const newKey = selectedKey + "new";
        const updatedOriginal = { ...baseVariable };
        const newVariable = {
          ...baseVariable,
          type: "Multiple2DGrid",
          Rows: newRows,
          values: newValues,
          valueLabels: newValueLabels,
          varName: data.varNames,
          spsssyntax: data.spsssyntax || "",
        };
        setSelectedVariable(updatedOriginal);
        workingDataGroup = {
          ...workingDataGroup,
          [selectedKey]: updatedOriginal,
          [newKey]: newVariable,
        };
        setdataGroup((prev) => ({
          ...prev,
          [selectedKey]: updatedOriginal,
          [newKey]: newVariable,
        }));
        await apiService.post("/save_all_data_list", { projectId, dataGroup: workingDataGroup });
      }

      toast.success(`Variable saved! ${data.varNames.length} binary variables created.`);
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.errors.length} row(s) had errors and were skipped.`);
      }
    } catch (error) {
      toast.error("Error saving variable: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // "Average Mention" — for a Multiple2DGrid/3DGrid/4DGrid question, create ONE new
  // Numeric SPSS variable ("<key>sm") summing every row variable except rows flagged
  // exclusive, add it to dataGroup as a new question, and surface the equivalent
  // COMPUTE/SUM SPSS syntax via the same spsssyntax textbox used by Save Variable.
  const handleAverageMention = async () => {
    if (!selectedVariable || !selectedKey) return;
    const varNames = selectedVariable.varName || [];
    const rows = selectedVariable.Rows || [];
    if (varNames.length === 0 || rows.length === 0) {
      toast.warning("This question has no rows/varNames to sum.");
      return;
    }

    const confirmed = await confirm({
      title: "Create Average Mention Variable",
      message: `This will create a new Numeric SPSS variable "${selectedKey}sm" summing every non-exclusive row variable in "${selectedKey}", and add it as a new question. Proceed?`,
      confirmText: "Create Variable",
    });
    if (!confirmed) return;

    setLoading(true);
    try {
      const { data } = await apiService.post('/save-average-mention', {
        projectId,
        key: selectedKey,
        varNames,
        rows,
        client: JSON.stringify(selectedClient || {}),
        project: JSON.stringify(selectedProject || {}),
        user: powner || '',
      });

      if (!data.success) {
        toast.error("Error: " + (data.error || "Unknown error"));
        return;
      }

      const title = `Average Mention - ${selectedVariable.Qtitle || selectedKey}`;
      const newVariable = {
        type: "Numeric",
        Qtitle: title,
        tabSpectitle: title,
        basetitle: selectedVariable.basetitle || "All respondents",
        Logic: selectedVariable.Logic || "",
        varName: [{ Name: data.varName, Location: "0:0" }],
        min: 0,
        max: 999,
        spsssyntax: data.spsssyntax || "",
      };

      const updatedDataGroup = { ...dataGroup, [data.varName]: newVariable };
      setdataGroup(updatedDataGroup);
      await apiService.post("/save_all_data_list", { projectId, dataGroup: updatedDataGroup });

      toast.success(`Created "${data.varName}" (Average Mention). Select it from the question list to review.`);
    } catch (error) {
      toast.error("Error creating average mention variable: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Commit a conversion patch ({ remove, add }) from ConvertTypeModal / Duplicate
  // to the dataGroup and persist it. Keeps a snapshot for one-step undo.
  const applyConversionPatch = async (patch, label) => {
    setConvSnapshot({ dataGroup: { ...dataGroup }, key: selectedKey });
    const updated = { ...dataGroup };
    (patch.remove || []).forEach((k) => delete updated[k]);
    Object.assign(updated, patch.add);
    setdataGroup(updated);
    if ((patch.remove || []).includes(selectedKey)) {
      setSelectedVariable(null);
    } else if (patch.add[selectedKey]) {
      setSelectedVariable(patch.add[selectedKey]);
      setActiveTab(SPECIAL_ROWS[patch.add[selectedKey].type] ? "specialRows" : "properties");
    }
    setLoading(true);
    try {
      await apiService.post("/save_all_data_list", { projectId, dataGroup: updated });
      toast.success(`Applied: ${label}`);
    } catch (error) {
      toast.error("Error saving conversion: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const undoConversion = async () => {
    if (!convSnapshot) return;
    setLoading(true);
    try {
      setdataGroup(convSnapshot.dataGroup);
      setSelectedVariable(convSnapshot.dataGroup[convSnapshot.key] || null);
      await apiService.post("/save_all_data_list", { projectId, dataGroup: convSnapshot.dataGroup });
      setConvSnapshot(null);
      toast.success("Conversion undone.");
    } catch (error) {
      toast.error("Error undoing conversion: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    if (!selectedVariable) return;
    const patch = duplicateVariable(selectedVariable, selectedKey, dataGroup);
    await applyConversionPatch(patch, `duplicated as ${patch.newKey}`);
  };

  // Single2DGrid → Custom (OE Combo): one custom row per value, with logic
  // OR-ing that value across every grid varName — e.g. Var1=1 or Var2=1 or Var3=1.
  // Values are removed (stashed) once converted.
  const handleOECombo = async () => {
    if (!selectedVariable || selectedVariable.type !== 'Single2DGrid') return;

    const values = selectedVariable.values || [];
    const varNames = selectedVariable.varName || [];

    if (values.length === 0) {
      toast.warning("No values defined to convert.");
      return;
    }
    if (varNames.length === 0) {
      toast.warning("No varNames defined to build logic from.");
      return;
    }

    const confirmed = await confirm({
      title: "Convert to Custom (OE Combo)",
      message: `This will convert "${selectedKey}" to Custom type, creating ${values.length} custom row(s) — one per value — with logic OR-ing that value across all ${varNames.length} grid variable(s). Values will be removed. Proceed?`,
      confirmText: "Convert",
    });
    if (!confirmed) return;

    const newCustomRows = values.map((val) => ({
      index: Number(val.index),
      label: val.title || String(val.index),
      logic: varNames.map((vn) => `${vn.Name}=${val.index}`).join(" or "),
      logicBase: "",
      norank: false,
      fix: false,
    }));

    const { values: _removedValues, valueLabels: _removedValueLabels, ...rest } = selectedVariable;
    const updatedVariable = {
      ...rest,
      type: "Custom",
      customRows: [...(selectedVariable.customRows || []), ...newCustomRows],
      _savedValues: values,
      _savedValueLabels: selectedVariable.valueLabels || {},
    };

    const patch = { remove: [], add: { [selectedKey]: updatedVariable } };
    await applyConversionPatch(patch, `${selectedKey} converted to Custom (OE Combo)`);
  };

  // Combine Variables: pick any number of SingleResponse + Multiple2DGrid questions and
  // build ONE new Custom question. Rows are the union of unique value/row titles
  // (case-insensitive, trimmed) across every selected question; each row's logic ORs
  // together `varname=index` (SingleResponse) / `varname=1` (Multiple2DGrid) for every
  // source that produced that title. Other properties are taken from the first selected
  // question — mirrors handleOECombo, no new SPSS variables/backend calls required.
  const combineCandidates = Object.entries(dataGroup || {})
    .filter(([, v]) => v && (v.type === 'SingleResponse' || v.type === 'Multiple2DGrid'));

  const handleCreateCombinedVariable = async () => {
    if (combineSelectedKeys.length === 0) {
      toast.warning("Select at least one variable to combine.");
      return;
    }

    const buckets = []; // [{ titleKey, title, logicParts: [] }]
    const addRow = (title, logicPart) => {
      const titleKey = String(title).trim().toLowerCase();
      let bucket = buckets.find((b) => b.titleKey === titleKey);
      if (!bucket) {
        bucket = { titleKey, title: String(title).trim(), logicParts: [] };
        buckets.push(bucket);
      }
      bucket.logicParts.push(logicPart);
    };

    combineSelectedKeys.forEach((key) => {
      const v = dataGroup[key];
      if (!v) return;
      if (v.type === 'SingleResponse') {
        const varNameEntry = (v.varName || [])[0];
        if (!varNameEntry) return;
        (v.values || []).forEach((val) => {
          addRow(val.title || String(val.index), `${varNameEntry.Name}=${val.index}`);
        });
      } else if (v.type === 'Multiple2DGrid') {
        (v.Rows || []).forEach((row) => {
          const varNameEntry = (v.varName || []).find((vn) => String(vn.row) === String(row.index));
          if (!varNameEntry) return;
          addRow(row.title || String(row.index), `${varNameEntry.Name}=1`);
        });
      }
    });

    if (buckets.length === 0) {
      toast.warning("None of the selected variables have any values/rows to combine.");
      return;
    }

    const newCustomRows = buckets.map((b, i) => ({
      index: i + 1,
      label: b.title,
      logic: b.logicParts.join(" or "),
      logicBase: "",
      norank: false,
      fix: false,
    }));

    const firstKey = combineSelectedKeys[0];
    // Keep varName from the first selected variable — the dashboard's live-preview
    // computation for type "Custom" unconditionally reads payload.varName[0].Name as a
    // structural placeholder (it doesn't drive actual row values — customRows[i].logic
    // does, via _compute_cr_data_for_col in dashboard.py) and errors if varName is empty.
    // Rows/Cols/values/valueLabels ARE stripped: leaving a Multiple2DGrid's Rows in place
    // would double up with the new customRows in the row-props merge (dashboard.py's
    // _get_row_props reads both Rows and customRows together).
    const { values, valueLabels, Rows, Cols, customRows, ...rest } = dataGroup[firstKey] || {};
    const title = combineTitle.trim() || `${rest.Qtitle || firstKey} (Combined)`;
    const newVariable = {
      ...rest,
      type: "Custom",
      Qtitle: title,
      tabSpectitle: title,
      customRows: newCustomRows,
    };

    const baseKey = combineSelectedKeys.join("_");
    let newKey = baseKey;
    let n = 2;
    while (dataGroup[newKey]) {
      newKey = `${baseKey}_${n}`;
      n++;
    }

    setLoading(true);
    try {
      const updatedDataGroup = { ...dataGroup, [newKey]: newVariable };
      setdataGroup(updatedDataGroup);
      await apiService.post("/save_all_data_list", { projectId, dataGroup: updatedDataGroup });
      setCombineSelectedKeys([]);
      setCombineTitle("");
      toast.success(`Created "${newKey}" with ${newCustomRows.length} combined row(s). Select it from the question list to review.`);
    } catch (error) {
      toast.error("Error creating combined variable: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSwapRowsAndCols = async () => {
    if (!selectedVariable?.Rows || !selectedVariable?.Cols) {
      toast.warning("Cannot swap: Missing rows or columns data");
      return;
    }

    const isGridType = selectedVariable.type && 
      (selectedVariable.type.includes("2DGrid") || 
       selectedVariable.type.includes("3DGrid") ||
       selectedVariable.type.includes("Grid"));

    if (!isGridType) {
      toast.warning("Cannot swap: Only grid-type variables support row/column swapping");
      return;
    }

    const confirmSwap = await confirm({
      title: "Swap Rows and Columns",
      message: `Are you sure you want to swap ${selectedVariable.Rows.length} rows with ${selectedVariable.Cols.length} columns?`,
      confirmText: "Swap",
    });
    
    if (!confirmSwap) return;

    const oldRows = JSON.parse(JSON.stringify(selectedVariable.Rows || []));
    const oldCols = JSON.parse(JSON.stringify(selectedVariable.Cols || []));

    const newRows = oldCols.map(col => ({
      index: col.index,
      title: col.title,
      exclusive: col.exclusive || false,
      ...Object.fromEntries(
        Object.entries(col).filter(([key]) => !['index', 'title', 'exclusive'].includes(key))
      )
    }));

    const newCols = oldRows.map(row => ({
      index: row.index,
      title: row.title,
      ...Object.fromEntries(
        Object.entries(row).filter(([key]) => !['index', 'title', 'exclusive'].includes(key))
      )
    }));

    setSelectedVariable(prev => ({
      ...prev,
      Rows: newRows,
      Cols: newCols,
      ...(prev.rowCount && { rowCount: prev.colCount }),
      ...(prev.colCount && { colCount: prev.rowCount })
    }));

    toast.success(`Swapped: ${oldRows.length} rows ↔ ${oldCols.length} cols → now ${newRows.length} rows, ${newCols.length} cols`);
  };

  const createNet = (label, rowIndices) => {
    const newNet = { label, indices: rowIndices, index: getNextSyntheticIndex(selectedVariable?.Nets) };
    setNets((prevNets) => {
      const updatedNets = [...prevNets, newNet];
      addNet(updatedNets);
      return updatedNets;
    });
  };

  const addNet = (updatedNets) => {
    setSelectedVariable((prev) => ({ ...prev, Nets: updatedNets }));
  };

  const saveChanges = async () => {
    if (!selectedVariable) return;

    setLoading(true);
    
    try {
      const updatedDataGroup = { ...dataGroup, [selectedKey]: selectedVariable };
      setdataGroup((prev) => ({ ...prev, [selectedKey]: selectedVariable }));
      setNets([]);

      await apiService.post("/save_all_data_list", { projectId, dataGroup: updatedDataGroup });
      toast.success("Data saved successfully");
    } catch (error) {
      toast.error("Error saving changes: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRowsChange = (updatedRows) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Rows: updatedRows }));
    }
  };

  const handleColsChange = (updatedCols) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Cols: updatedCols }));
    }
  };

  const handlestatementsChange = (updatedStatements) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Statements: updatedStatements }));
    }
  };

  const handlevaluesChange = (updatedValues) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, values: updatedValues }));
    }
  };

  const handlevalueLabelsChange = (updatedvalueLabels) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, valueLabels: updatedvalueLabels }));
    }
  };

  const handleNetPropertyChange = (e, property) => {
    if (!selectedVariable) return;

    const newValue = e.target.value;
    
    const processNetValue = (value) => {
      if (!value || typeof value !== 'string') return [];
      
      const netDefinitions = value.split(';').map(part => part.trim()).filter(part => part !== '');
      const nets = [];
      
      netDefinitions.forEach(definition => {
        const parts = definition.split('=').map(part => part.trim());
        
        if (parts.length >= 2) {
          const label = parts[0];
          const indicesPart = parts[1];
          const indices = processIndicesString(indicesPart);
          nets.push({ label, indices });
        } else if (parts.length === 1) {
          const indices = processIndicesString(parts[0]);
          nets.push({ label: parts[0], indices: indices.length > 0 ? indices : [] });
        }
      });
      
      return nets;
    };

    const processIndicesString = (str) => {
      if (!str || typeof str !== 'string') return [];
      
      const result = [];
      const parts = str.split(',').map(part => part.trim());
      
      parts.forEach(part => {
        if (part.includes('-')) {
          const rangeParts = part.split('-').map(p => p.trim());
          if (rangeParts.length === 2) {
            const start = parseInt(rangeParts[0], 10);
            const end = parseInt(rangeParts[1], 10);
            if (!isNaN(start) && !isNaN(end)) {
              for (let i = start; i <= end; i++) result.push(i);
            }
          }
        } else {
          const num = parseInt(part, 10);
          if (!isNaN(num)) result.push(num);
        }
      });
      
      return [...new Set(result)].sort((a, b) => a - b);
    };

    const processedNets = processNetValue(newValue);
    setSelectedVariable((prev) => ({ ...prev, [property]: newValue, Nets: processedNets }));
  };

  const handlePropertyChange = (e, property) => {
    if (!selectedVariable) return;
    const newValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSelectedVariable((prev) => {
      const next = { ...prev, [property]: newValue };
      const alias = PROPERTY_ALIASES[property];
      if (alias) delete next[alias];
      return next;
    });
  };

  const renderTabContent = () => {
    if (!selectedVariable) return <div>No variable data available</div>;

    switch (activeTab) {
      case "properties":
        return (
          <div className="properties-container">

            {/* ── Question Title banner ── */}
            {selectedVariable?.Qtitle && (
              <div className="prop-qtitle-banner">
                <span className="prop-qtitle-label">Q</span>
                <span className="prop-qtitle-text">{selectedVariable.Qtitle}</span>
              </div>
            )}

            {/* ── SPSS Syntax (generated when a Custom variable is saved as new variables) ── */}
            {getPropertyValue('spsssyntax', '') && (
              <div className="prop-section">
                <div className="prop-section-header">
                  <span className="prop-section-icon">⌨</span>
                  <span className="prop-section-title">SPSS Syntax</span>
                </div>
                <textarea
                  readOnly
                  className="prop-text-input"
                  style={{ width: "100%", minHeight: 140, fontFamily: "monospace", resize: "vertical" }}
                  value={getPropertyValue('spsssyntax', '')}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  className="cr-add-btn"
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    navigator.clipboard.writeText(getPropertyValue('spsssyntax', ''));
                    toast.success("SPSS syntax copied to clipboard.");
                  }}
                >
                  Copy to Clipboard
                </button>
              </div>
            )}
          </div>
        );

      case "checkboxes":
        return (
          <div className="properties-container">
            {/* ── Boolean toggles ── */}
            <div className="prop-section">
              <div className="prop-section-header">
                <span className="prop-section-icon">⚙</span>
                <span className="prop-section-title">Settings</span>
              </div>
              <div className="prop-toggle-grid">
                {Object.entries(allBooleanProperties).map(([property, defaultValue]) => {
                  if (!isPropertyVisibleForType(property, selectedVariable.type)) return null;
                  // showNetswithinLabels only makes sense once the question has Nets defined.
                  if (property === 'showNetswithinLabels' && !(selectedVariable?.Nets?.length > 0)) return null;
                  const isOn = getPropertyValue(property, defaultValue);
                  return (
                    <label
                      key={property}
                      className={`prop-toggle-card ${isOn ? "prop-toggle-card--on" : ""}`}
                      title={property}
                    >
                      <span className="prop-toggle-name">{TOGGLE_LABELS[property] || property}</span>
                      <div className={`prop-toggle-track ${isOn ? "prop-toggle-track--on" : ""}`}>
                        <div className="prop-toggle-thumb" />
                      </div>
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={(e) => handlePropertyChange(e, property)}
                        style={{ display: "none" }}
                      />
                    </label>
                  );
                })}
              </div>
            </div>

            {/* ── Ranking ── */}
            <div className="prop-section">
              <div className="prop-section-header">
                <span className="prop-section-icon">↕</span>
                <span className="prop-section-title">Ranking</span>
              </div>
              <div className="prop-toggle-grid">
                {[
                  { key: 'rank',             label: 'Rank' },
                  { key: 'rankalphabetical', label: 'Alphabetical' },
                  { key: 'netswithingroups', label: 'Nets in Groups' },
                ].filter(({ key }) => key !== 'netswithingroups' || (selectedVariable?.Nets?.length > 0))
                 .map(({ key, label }) => {
                  const isOn = getPropertyValue(key, false);
                  return (
                    <label
                      key={key}
                      className={`prop-toggle-card ${isOn ? "prop-toggle-card--on" : ""}`}
                      title={key}
                    >
                      <span className="prop-toggle-name">{label}</span>
                      <div className={`prop-toggle-track ${isOn ? "prop-toggle-track--on" : ""}`}>
                        <div className="prop-toggle-thumb" />
                      </div>
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={(e) => handlePropertyChange(e, key)}
                        style={{ display: "none" }}
                      />
                    </label>
                  );
                })}
              </div>
              {getPropertyValue('rank', false) && !getPropertyValue('rankalphabetical', false) && (
                <div className="prop-rank-config">
                  <div className="prop-text-field">
                    <label className="prop-text-label">Rank Col (0 = Total)</label>
                    <input
                      type="number"
                      min="0"
                      className="prop-text-input"
                      value={getPropertyValue('rankcol', 0)}
                      onChange={(e) => setPropertyValue('rankcol', parseInt(e.target.value, 10) || 0)}
                    />
                  </div>
                  <div className="prop-rank-radio">
                    <label className="prop-rank-radio-option">
                      <input
                        type="radio"
                        name={`rankby-${selectedKey}`}
                        checked={!getPropertyValue('rankbyfreq', false)}
                        onChange={() => setPropertyValue('rankbyfreq', false)}
                      />
                      <span>% (Percentage)</span>
                    </label>
                    <label className="prop-rank-radio-option">
                      <input
                        type="radio"
                        name={`rankby-${selectedKey}`}
                        checked={getPropertyValue('rankbyfreq', false)}
                        onChange={() => setPropertyValue('rankbyfreq', true)}
                      />
                      <span>Freq (Count)</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case "chart":
        return (
          <div className="properties-container">
            {/* ── Add to Chart (shown for all question types) ── */}
            {(
              <div className="prop-section">
                <div className="prop-section-header">
                  <span className="prop-section-icon">📊</span>
                  <span className="prop-section-title">Add to Chart</span>
                </div>

                <div className="prop-text-grid">
                  <div className="prop-text-field">
                    <label className="prop-text-label">Chart type</label>
                    <select
                      className="prop-text-input"
                      value={chartConfig.chartType}
                      onChange={(e) => updateChartConfig({ chartType: e.target.value })}
                    >
                      <option value="doughnut">Doughnut</option>
                      <option value="bar">Bar (clustered)</option>
                      <option value="column">Column (vertical bar)</option>
                      <option value="stackedbar">Stacked bar</option>
                    </select>
                  </div>

                  <div className="prop-text-field">
                    <label className="prop-text-label">Chart against</label>
                    <select
                      className="prop-text-input"
                      value={chartConfig.bannerScope}
                      onChange={(e) => updateChartConfig({ bannerScope: e.target.value })}
                    >
                      <option value="main">Main banner (whole sample)</option>
                      <option value="targets">Targets (one chart per banner point)</option>
                    </select>
                  </div>

                  <div className="prop-text-field">
                    <label className="prop-text-label">
                      Banner{chartConfig.bannerScope === 'main' ? ' (for sig-test comparison)' : ''}
                    </label>
                    <select
                      className="prop-text-input"
                      value={chartConfig.bannerName || ''}
                      onChange={(e) => updateChartConfig({ bannerName: e.target.value || null })}
                    >
                      <option value="">— none —</option>
                      {(banners || []).map((b) => (
                        <option key={b.name} value={b.name}>{b.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="prop-text-field">
                    <label className="prop-text-label">Sig-test display</label>
                    <select
                      className="prop-text-input"
                      value={chartConfig.sigDisplay}
                      onChange={(e) => updateChartConfig({ sigDisplay: e.target.value })}
                    >
                      <option value="none">None</option>
                      <option value="symbols">Symbols (letters/stars on chart)</option>
                      <option value="boxes">Boxes (+/- callout summary)</option>
                    </select>
                  </div>
                </div>

                {/* Bar/doughnut only: nets inline vs. shown separately */}
                {(chartConfig.chartType === 'bar' || chartConfig.chartType === 'doughnut') && (
                  <div style={{ marginTop: 10 }}>
                    <label
                      style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
                      title={chartConfig.chartType === 'doughnut'
                        ? "When on, nets are plotted as an extra slice in the doughnut. When off (default), nets show as text in the doughnut's center hole."
                        : "When on, nets are plotted as extra bars inside the chart. When off (default), nets show as a small textbox next to the rows they summarize."}
                    >
                      <div
                        onClick={() => updateChartConfig({ netsInChart: !chartConfig.netsInChart })}
                        style={{
                          width: 34, height: 18, borderRadius: 10, flexShrink: 0,
                          background: chartConfig.netsInChart ? '#2563eb' : '#334155',
                          position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                        }}
                      >
                        <div style={{
                          position: 'absolute', top: 2, left: chartConfig.netsInChart ? 18 : 2,
                          width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                        }} />
                      </div>
                      <span style={{ fontSize: 12, color: chartConfig.netsInChart ? '#93c5fd' : '#8a9bb0' }}>
                        Show nets within chart
                      </span>
                    </label>
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                      {chartConfig.chartType === 'doughnut'
                        ? "Off (default): nets appear as text in the doughnut's center hole. On: nets are plotted as an extra slice instead."
                        : "Off (default): nets appear as a textbox beside the rows they summarize. On: nets are plotted as regular bars inside the chart instead."}
                    </div>
                  </div>
                )}

                {/* Stacked chart only: orientation + which rows/values to leave out of the series.
                    For Single2DGrid/Numeric2DGrid this also switches on the statements-as-categories,
                    rating-values-as-series layout (see chartExport.py render_rating_grid_slides). */}
                {chartConfig.chartType === 'stackedbar' && (
                  <div style={{ marginTop: 12 }}>
                    <div className="prop-text-field">
                      <label className="prop-text-label">Stacked orientation</label>
                      <select
                        className="prop-text-input"
                        value={chartConfig.stackedOrientation || 'horizontal'}
                        onChange={(e) => updateChartConfig({ stackedOrientation: e.target.value })}
                      >
                        <option value="horizontal">Horizontal (stacked bar)</option>
                        <option value="vertical">Vertical (stacked column)</option>
                      </select>
                    </div>

                    <div style={{ fontSize: 11, color: '#8a9bb0', marginTop: 10, marginBottom: 6 }}>
                      Exclude rows/values from the chart (unchecked = included):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {excludableRows.length === 0 && (
                        <span style={{ fontSize: 10, color: '#475569' }}>No rows/values found on this question.</span>
                      )}
                      {excludableRows.map((r) => {
                        const key = String(r.index);
                        const excluded = (chartConfig.excludedRowIndices || []).includes(key);
                        return (
                          <button
                            key={key}
                            onClick={() => toggleExcludedRow(r.index)}
                            title={r.title}
                            style={{
                              fontSize: 10, padding: '3px 8px', borderRadius: 12,
                              border: excluded ? '1px solid #dc2626' : '1px solid #334155',
                              background: excluded ? '#3f1d1d' : 'transparent',
                              color: excluded ? '#fca5a5' : '#8a9bb0', cursor: 'pointer',
                              textDecoration: excluded ? 'line-through' : 'none',
                            }}
                          >
                            {r.title || key}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Column chart on the main banner: Mean/Std Dev header + Nets-or-Mean callout,
                    matching the numeric-question reference layout. */}
                {chartConfig.chartType === 'column' && chartConfig.bannerScope === 'main' && (
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 8 }}>
                    Column charts on the main banner show Mean / Std Dev above the chart when
                    this question has those stats enabled. With "Boxes" sig display, the callout
                    uses this question's Nets if it has any; otherwise it falls back to a
                    Mean-vs-Total callout (needs Std Dev enabled too, to test significance).
                  </div>
                )}

                {/* Sig/Compliment header-text filter — only meaningful for main-banner (whole
                    sample) charts, where the sig-box callout otherwise scans every banner
                    point on the resolved banner. */}
                {chartConfig.bannerScope === 'main' && (
                  <div style={{ marginTop: 12 }}>
                    <div className="prop-text-field">
                      <label className="prop-text-label">
                        Sig/Compliment header filter (banner headerText, separated by ;)
                      </label>
                      <input
                        type="text"
                        className="prop-text-input"
                        value={chartConfig.sigHeaderFilter || ""}
                        onChange={(e) => updateChartConfig({ sigHeaderFilter: e.target.value })}
                        placeholder="e.g. Age; Gender; Region — blank = all banner points"
                      />
                    </div>
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                      When set, sig-test and compliment-test callouts only draw from banner
                      points whose header text matches one of these (semicolon-separated,
                      case-insensitive). Leave blank to use every banner point, as before.
                    </div>
                  </div>
                )}

                {/* Slide grouping — only meaningful when charting per banner point */}
                {chartConfig.bannerScope === 'targets' && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, color: '#8a9bb0', marginBottom: 6 }}>
                      Group banner points into slides. Each group becomes one slide with one chart
                      per selected point (points can repeat across groups).
                      {!chartConfig.bannerName && ' Pick a banner above first.'}
                    </div>
                    {(chartConfig.slideGroups || []).map((group) => (
                      <div key={group.id} style={{
                        background: '#0c1928', border: '1px solid #1e3a5f', borderRadius: 8,
                        padding: '8px 10px', marginBottom: 8,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <input
                            value={group.label}
                            onChange={(e) => updateSlideGroup(group.id, { label: e.target.value })}
                            style={{
                              flex: 1, background: 'transparent', border: 'none',
                              color: '#e2e8f0', fontSize: 12, fontWeight: 600, outline: 'none',
                            }}
                          />
                          <button
                            onClick={() => removeSlideGroup(group.id)}
                            title="Remove this slide group"
                            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14 }}
                          >×</button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {chartBannerPoints.length === 0 && (
                            <span style={{ fontSize: 10, color: '#475569' }}>No points on this banner.</span>
                          )}
                          {chartBannerPoints.map((pt) => {
                            const active = (group.pointLetters || []).includes(pt.letter);
                            return (
                              <button
                                key={pt.letter}
                                onClick={() => toggleSlideGroupPoint(group.id, pt.letter)}
                                title={pt.text}
                                style={{
                                  fontSize: 10, padding: '3px 8px', borderRadius: 12,
                                  border: active ? '1px solid #2563eb' : '1px solid #334155',
                                  background: active ? '#1e3a5f' : 'transparent',
                                  color: active ? '#93c5fd' : '#8a9bb0', cursor: 'pointer',
                                }}
                              >
                                {pt.letter}: {pt.text}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={addSlideGroup}
                      disabled={!chartConfig.bannerName}
                      style={{
                        width: '100%', background: '#1e3a5f', border: '1px dashed #334155',
                        color: '#60a5fa', borderRadius: 7, padding: '6px 0', fontSize: 12,
                        cursor: chartConfig.bannerName ? 'pointer' : 'not-allowed', opacity: chartConfig.bannerName ? 1 : 0.5,
                      }}
                    >
                      + Add Slide Group
                    </button>
                  </div>
                )}

                {/* Chart colors. Global PPT color is deck-level — shared by every chart in
                    chartDeckTarget (old and new slides alike, see commitDeckGlobalColor /
                    the sync useEffect above). Stub colors + the shades checkbox are THIS
                    chart's own chartConfig fields, so they're independent per question. */}
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, color: '#8a9bb0', marginBottom: 6 }}>
                    Chart colors (optional)
                  </div>
                  <div className="prop-text-grid">
                    <div className="prop-text-field">
                      <label className="prop-text-label">Global PPT color (shared by the whole deck)</label>
                      <input
                        type="text"
                        className="prop-text-input"
                        value={globalPptColor}
                        onChange={(e) => setGlobalPptColor(e.target.value)}
                        onBlur={(e) => commitDeckGlobalColor(e.target.value)}
                        placeholder="#1DAFAD — applied to every chart in this deck"
                      />
                    </div>
                    <div className="prop-text-field">
                      <label className="prop-text-label">Stub colors for this chart (semicolon-separated)</label>
                      <input
                        type="text"
                        className="prop-text-input"
                        value={chartConfig.stubColors || ""}
                        onChange={(e) => updateChartConfig({ stubColors: e.target.value })}
                        placeholder="#FF0000; #00FF00; or Non: #FF00FF"
                        title="Entries are separated by ';' (not ',') since stub names can contain commas. Plain colors assign in stub order, e.g. #FF0000; #00FF00; #0000FF. To target one stub by name without setting the rest, use 'Stub Name: #color', e.g. Non: #FF00FF — you can mix both in the same list."
                      />
                    </div>
                  </div>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', marginTop: 10 }}
                    title="Generates progressively lighter shades of the Global PPT color, one per stub, for THIS chart only. Only used when this chart's Stub colors is blank."
                  >
                    <div
                      onClick={() => updateChartConfig({ autoShades: !chartConfig.autoShades })}
                      style={{
                        width: 34, height: 18, borderRadius: 10, flexShrink: 0,
                        background: chartConfig.autoShades ? '#2563eb' : '#334155',
                        position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                      }}
                    >
                      <div style={{
                        position: 'absolute', top: 2, left: chartConfig.autoShades ? 18 : 2,
                        width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                      }} />
                    </div>
                    <span style={{ fontSize: 12, color: chartConfig.autoShades ? '#93c5fd' : '#8a9bb0' }}>
                      Generate Lighter Shades Automatically (this chart only)
                    </span>
                  </label>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', marginTop: 10 }}
                    title="Opts THIS chart out of the deck's Global PPT Color — it won't get the flat global fill or any shades generated from it. This chart's own Stub Colors still apply; any stub left uncustomized falls back to the default chart colors instead. Other charts in the deck are unaffected."
                  >
                    <div
                      onClick={() => updateChartConfig({ noGlobalColor: !chartConfig.noGlobalColor })}
                      style={{
                        width: 34, height: 18, borderRadius: 10, flexShrink: 0,
                        background: chartConfig.noGlobalColor ? '#2563eb' : '#334155',
                        position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                      }}
                    >
                      <div style={{
                        position: 'absolute', top: 2, left: chartConfig.noGlobalColor ? 18 : 2,
                        width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                      }} />
                    </div>
                    <span style={{ fontSize: 12, color: chartConfig.noGlobalColor ? '#93c5fd' : '#8a9bb0' }}>
                      Not Global Color (exclude this chart from the deck's Global PPT Color)
                    </span>
                  </label>
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                    Priority per stub: a named override for that stub (e.g. Non: #FF00FF) &gt; this
                    chart's Stub colors &gt; this chart's auto-generated shades of the deck's global
                    color &gt; the deck's global color &gt; default chart colors. "Not Global Color"
                    removes the last two steps for this chart only.
                  </div>
                </div>

                {/* Sorting — independent per chart, like Stub Colors */}
                <div style={{ marginTop: 14 }}>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
                    title="Reorders this chart's stubs/statements by percentage. Off by default (original row order)."
                  >
                    <div
                      onClick={() => updateChartConfig({ sortEnabled: !chartConfig.sortEnabled })}
                      style={{
                        width: 34, height: 18, borderRadius: 10, flexShrink: 0,
                        background: chartConfig.sortEnabled ? '#2563eb' : '#334155',
                        position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                      }}
                    >
                      <div style={{
                        position: 'absolute', top: 2, left: chartConfig.sortEnabled ? 18 : 2,
                        width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                      }} />
                    </div>
                    <span style={{ fontSize: 12, color: chartConfig.sortEnabled ? '#93c5fd' : '#8a9bb0' }}>
                      Sorting (this chart only)
                    </span>
                  </label>

                  {chartConfig.sortEnabled && (
                    <div style={{ marginTop: 10 }}>
                      <div className="prop-text-field">
                        <label className="prop-text-label">Sort mode</label>
                        <select
                          className="prop-text-input"
                          value={chartConfig.sortMode || "normal"}
                          onChange={(e) => updateChartConfig({ sortMode: e.target.value })}
                        >
                          <option value="normal">Normal Sorting (% high to low)</option>
                          <option value="netsAsNet">Nets and Statement Equal Sentiment as Net Sorting</option>
                        </select>
                      </div>
                      <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, marginBottom: 10 }}>
                        {chartConfig.sortMode === "netsAsNet"
                          ? "Nets are sorted by their own % (high to low); each Net's statements are then sorted by % within it."
                          : "All stubs sorted by % high to low. \"Other\", \"None of these\", \"Don't know\", and \"Can't remember\" always stay at the bottom, in their original order."}
                      </div>
                      <div className="prop-text-field">
                        <label className="prop-text-label">Exclude Stub From Sorting (semicolon-separated)</label>
                        <input
                          type="text"
                          className="prop-text-input"
                          value={chartConfig.sortExcludeStubs || ""}
                          onChange={(e) => updateChartConfig({ sortExcludeStubs: e.target.value })}
                          placeholder="Other; None of these; Don't know; Can't remember"
                          title="These stubs skip percentage sorting and always end up last, in the order you type them here. They're added on top of the always-trailing defaults (Other/None of these/Don't know/Can't remember) — works the same in both sort modes."
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Add to Deck */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
                  <input
                    list="chart-deck-names"
                    value={chartDeckTarget}
                    onChange={(e) => setChartDeckTarget(e.target.value)}
                    placeholder="Deck name (existing or new)"
                    className="prop-text-input"
                    style={{ flex: 1 }}
                  />
                  <datalist id="chart-deck-names">
                    {(chartDecks || []).map((d) => <option key={d.name} value={d.name} />)}
                  </datalist>
                  <CommonButton
                    label={chartAddStatus === 'done' ? 'Added ✓' : 'Add to Deck'}
                    onClick={addChartToDeck}
                    disabled={!chartDeckTarget.trim() || chartAddStatus === 'saving'}
                    variant="blue"
                    size="small"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case "fields":
        return (
          <div className="properties-container">
            {/* ── Stats Rows (Multiple2DGrid / Multiple3DGrid only, when mean or allstats is on) ── */}
            {(selectedVariable.type === 'Multiple2DGrid' || selectedVariable.type === 'Multiple3DGrid' || selectedVariable.type === 'Custom') &&
              (getPropertyValue('mean', false) || getPropertyValue('allstats', false)) && (
              <div className="prop-section">
                <div className="prop-section-header">
                  <span className="prop-section-icon">∑</span>
                  <span className="prop-section-title">Stat Rows</span>
                </div>
                <div className="prop-text-grid">
                  <div className="prop-text-field prop-text-field--wide">
                    <label className="prop-text-label">Row indices for stats (e.g. 1-7 or 1,3,4)</label>
                    <input
                      type="text"
                      className="prop-text-input"
                      value={getPropertyValue('statsRows', (() => {
                        const idxs = (selectedVariable.Rows || [])
                          .map(r => Number(r.index)).filter(i => !isNaN(i) && i > 0);
                        return idxs.length ? `${Math.min(...idxs)}-${Math.max(...idxs)}` : '';
                      })())}
                      placeholder="e.g. 1-7 or 1,3,4"
                      onChange={(e) => handlePropertyChange(e, 'statsRows')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── Text fields ── */}
            <div className="prop-section">
              <div className="prop-section-header">
                <span className="prop-section-icon">✎</span>
                <span className="prop-section-title">Configuration</span>
              </div>
              <div className="prop-text-grid">
                <div className="prop-text-field">
                  <label className="prop-text-label">Type</label>
                  <select
                    className="prop-text-input"
                    value={getPropertyValue("type", "")}
                    onChange={(e) => handlePropertyChange(e, "type")}
                  >
                    {!ALL_QUESTION_TYPES.includes(getPropertyValue("type", "")) && (
                      <option value={getPropertyValue("type", "")}>{getPropertyValue("type", "") || "—"}</option>
                    )}
                    {ALL_QUESTION_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                {[
                  // { key: "table",        label: "Table",          placeholder: "freq" }, // commented out for now — see PropertiesComponent type map
                  { key: "Qtitle",       label: "Q Title",        placeholder: "Question title..." },
                  { key: "tabSpectitle", label: "Tab Spec Title", placeholder: "Title shown in the table..." },
                  { key: "basetitle",    label: "Base Title",     placeholder: "All respondents" },
                  { key: "Logic",        label: "Logic",          placeholder: "" },
                  // { key: "combo",        label: "Combo",          placeholder: "S3 or S4, S3 and S4;S3 and S4" }, // commented out for now — see PropertiesComponent type map
                  // { key: "flttb",        label: "Flttb",          placeholder: "S3, S4=1-4 etc" }, // commented out for now — see PropertiesComponent type map
                  // { key: "stacked",      label: "Stacked",        placeholder: "1,3,4;1,6,9" }, // commented out for now — see PropertiesComponent type map
                ].map(({ key, label, placeholder }) => (
                  <div key={key} className="prop-text-field">
                    <label className="prop-text-label">{label}</label>
                    <input
                      type="text"
                      className="prop-text-input"
                      value={getPropertyValue(key, "")}
                      placeholder={placeholder}
                      onChange={(e) => handlePropertyChange(e, key)}
                    />
                  </div>
                ))}

                <div className="prop-text-field">
                  <label className="prop-text-label">Min</label>
                  <input
                    type="number"
                    className="prop-text-input"
                    value={getPropertyValue("min", "")}
                    placeholder="0"
                    onChange={(e) => handlePropertyChange(e, "min")}
                  />
                </div>
                <div className="prop-text-field">
                  <label className="prop-text-label">Max</label>
                  <input
                    type="number"
                    className="prop-text-input"
                    value={getPropertyValue("max", "")}
                    placeholder="99999999"
                    onChange={(e) => handlePropertyChange(e, "max")}
                  />
                </div>

                {/* nets uses special handler — commented out for now, see PropertiesComponent type map
                <div className="prop-text-field prop-text-field--wide">
                  <label className="prop-text-label">Nets (text definition)</label>
                  <input
                    type="text"
                    className="prop-text-input"
                    value={getPropertyValue("nets", "")}
                    placeholder="NET1=1-5;NET2=6-10;11-15"
                    onChange={(e) => handleNetPropertyChange(e, "nets")}
                  />
                </div>
                */}
                                <div className="prop-text-field prop-text-field--wide">
                  <label className="prop-text-label">New Key</label>
                  <input
                    type="text"
                    className="prop-text-input"
                    value={getPropertyValue("newkey", "")}
                    placeholder="Q1,Q2 etc"
                    onChange={(e) => handlePropertyChange(e, "newkey")}
                  />
                </div>
                <div className="prop-text-field prop-text-field--wide">
                  <label className="prop-text-label">Weight Variable</label>
                  <input
                    type="text"
                    className="prop-text-input"
                    value={getPropertyValue("weightvar", "")}
                    placeholder="Q1,Q2 etc"
                    onChange={(e) => handlePropertyChange(e, "weightvar")}
                  />
                </div>                
              </div>
            </div>
          </div>
        );

      case "rows": {
        const isGridRankType = selectedVariable.type === 'Multiple2DGrid' || selectedVariable.type === 'Multiple3DGrid';
        return selectedVariable.Rows?.length > 0 ? (
          <TableSettingModal
            isOpen={true}
            rows={selectedVariable.Rows}
            onClose={() => setActiveTab("properties")}
            onRowsChange={handleRowsChange}
            typeofproperty={"rows"}
            createNet={createNet}
            netLabel={netLabel}
            setNetLabel={setNetLabel}
            showRowFixNoRank={isGridRankType}
            inline={true}
          />
        ) : (
          <div className="no-data-message">No rows data available</div>
        );
      }

      case "cols":
        return selectedVariable.Cols?.length > 0 ? (
          <TableSettingModal
            isOpen={true}
            rows={selectedVariable.Cols}
            onClose={() => setActiveTab("properties")}
            onRowsChange={handleColsChange}
            typeofproperty={"cols"}
            createNet={createNet}
            netLabel={netLabel}
            setNetLabel={setNetLabel}
            inline={true}
          />
        ) : (
          <div className="no-data-message">No columns data available</div>
        );

      case "values":
        return selectedVariable.values?.length > 0 ? (
          <TableSettingModal
            isOpen={true}
            rows={selectedVariable.values}
            onClose={() => setActiveTab("properties")}
            onRowsChange={handlevaluesChange}
            typeofproperty={"values"}
            createNet={createNet}
            netLabel={netLabel}
            setNetLabel={setNetLabel}
            inline={true}
          />
        ) : (
          <div className="no-data-message">No values data available</div>
        );

      case "statements":
        return selectedVariable.Statements?.length > 0 ? (
          <TableSettingModal
            isOpen={true}
            rows={selectedVariable.Statements}
            onClose={() => setActiveTab("properties")}
            onRowsChange={handlestatementsChange}
            typeofproperty={"statements"}
            createNet={createNet}
            netLabel={netLabel}
            setNetLabel={setNetLabel}
            inline={true}
          />
        ) : (
          <div className="no-data-message">No statements data available</div>
        );

      case "valueLabels": {
        const valueLabelsArray = selectedVariable.valueLabels
          ? Object.entries(selectedVariable.valueLabels).map(([key, value]) => ({ index: key, title: value }))
          : [];
        return valueLabelsArray.length > 0 ? (
          <TableSettingModal
            isOpen={true}
            rows={valueLabelsArray}
            onClose={() => setActiveTab("properties")}
            onRowsChange={(updatedRows) => {
              const updatedValueLabels = {};
              updatedRows.forEach((row) => { updatedValueLabels[row.index] = row.title; });
              handlevalueLabelsChange(updatedValueLabels);
            }}
            typeofproperty={"valueLabels"}
            createNet={createNet}
            netLabel={netLabel}
            setNetLabel={setNetLabel}
            inline={true}
          />
        ) : (
          <div className="no-data-message">No value labels data available</div>
        );
      }

      case "customRows": {
        const customRowsList = selectedVariable?.customRows || [];

        const getMaxIndex = () => {
          const type = selectedVariable?.type || "";
          const isMultipleType = type.includes("Multiple") || type.includes("MultipleGrid");
          if (isMultipleType && selectedVariable?.Rows?.length > 0) {
            return Math.max(...selectedVariable.Rows.map(r => parseInt(r.index) || 0), 0);
          }
          if (selectedVariable?.values?.length > 0) {
            return Math.max(...selectedVariable.values.map(v => parseInt(v.index) || 0), 0);
          }
          return 0;
        };

        const addCustomRow = () => {
          const base = getMaxIndex();
          const nextIndex = base + customRowsList.length + 1;
          const updated = [...customRowsList, { index: nextIndex, label: "", logic: "", logicBase: "", norank: false, fix: false, rowCalc: false, formula: false }];
          setSelectedVariable(prev => ({ ...prev, customRows: updated }));
        };

        const updateCustomRow = (idx, field, value) => {
          const updated = customRowsList.map((r, i) => i === idx ? { ...r, [field]: value } : r);
          setSelectedVariable(prev => ({ ...prev, customRows: updated }));
        };

        // rowCalc and formula are mutually exclusive modes for the same Logic
        // field; turning one on clears the other. Both off = the original
        // boolean-filter custom row.
        const updateRowMode = (idx, mode, checked) => {
          const other = mode === 'rowCalc' ? 'formula' : 'rowCalc';
          const updated = customRowsList.map((r, i) =>
            i === idx ? { ...r, [mode]: checked, ...(checked ? { [other]: false } : {}) } : r
          );
          setSelectedVariable(prev => ({ ...prev, customRows: updated }));
        };

        const deleteCustomRow = (idx) => {
          const updated = customRowsList.filter((_, i) => i !== idx);
          setSelectedVariable(prev => ({ ...prev, customRows: updated }));
        };

        const handlePasteFromExcel = (e) => {
          const text = e.target.value;
          if (!text.trim()) return;
          const lines = text.trim().split('\n').filter(l => l.trim());
          const base = getMaxIndex();
          const parsed = [];
          lines.forEach((line, i) => {
            const parts = line.split('\t');
            let idx, label, logic, logicBase;
            if (parts.length === 1) {
              idx = base + customRowsList.length + i + 1;
              label = parts[0].trim();
              logic = ""; logicBase = "";
            } else if (parts.length === 2) {
              idx = parseInt(parts[0].trim()) || (base + customRowsList.length + i + 1);
              label = parts[1].trim();
              logic = ""; logicBase = "";
            } else if (parts.length === 3) {
              idx = parseInt(parts[0].trim()) || (base + customRowsList.length + i + 1);
              label = parts[1].trim();
              logic = parts[2].trim(); logicBase = "";
            } else {
              idx = parseInt(parts[0].trim()) || (base + customRowsList.length + i + 1);
              label = parts[1].trim();
              logic = parts[2].trim();
              logicBase = parts[3].trim();
            }
            if (label) parsed.push({ index: idx, label, logic, logicBase, norank: false, fix: false, rowCalc: false, formula: false });
          });
          if (parsed.length > 0) {
            const updated = [...customRowsList, ...parsed];
            setSelectedVariable(prev => ({ ...prev, customRows: updated }));
            e.target.value = '';
          }
        };

        return (
          <div className="cr-container">
            <div className="cr-header">
              <span className="cr-count-badge">
                {customRowsList.length} Custom Row{customRowsList.length !== 1 ? "s" : ""}
              </span>
              <button className="cr-add-btn" onClick={addCustomRow}>+ Add Row</button>
            </div>

            <div className="cr-paste-area">
              <label className="cr-paste-label">
                Paste from Excel (tab-separated: index | label | logic | logicBase — one row per line)
              </label>
              <textarea
                className="cr-paste-textarea"
                placeholder={"101\tCustom Row A\tQ1=1 AND Q2=2\tQ1=1\n102\tCustom Row B\tQ3=3\t"}
                onChange={handlePasteFromExcel}
              />
            </div>

            <table className="cr-table">
              <thead>
                <tr>
                  <th className="cr-th cr-th-index">Index</th>
                  <th className="cr-th cr-th-label">Label</th>
                  <th className="cr-th cr-th-logic">Logic</th>
                  <th className="cr-th cr-th-logic">Logic Base</th>
                  <th className="cr-th" style={{width:'6%'}}>No Rank</th>
                  <th className="cr-th" style={{width:'5%'}}>Fix</th>
                  <th className="cr-th" style={{width:'7%'}} title="Treat Logic as a Row Calc expression (R<n>/P<n> against other rows in this table) instead of a boolean filter">Row Calc</th>
                  <th className="cr-th" style={{width:'7%'}} title="Treat Logic as an aggregate formula over raw variables (Mean/Sum/Median), reduced to one value">Formula</th>
                  <th className="cr-th cr-th-del"></th>
                </tr>
              </thead>
              <tbody>
                {customRowsList.map((row, idx) => {
                  const isRowCalc = !!row.rowCalc;
                  const isFormula = !!row.formula;
                  const logicPlaceholder = isFormula
                    ? "Mean(Q4*3), Sum(Q4*3, Q5*4, Q2/2)"
                    : isRowCalc
                      ? "R1+R2, R1*R4 (row calc)"
                      : "Q1=1 AND Q2=2";
                  const logicTitle = isFormula
                    ? "Aggregate formula over raw variables (from table_single/grid_single/grid_multiple) — Mean/Sum/Median, both weighted & unweighted; missing values count as 0"
                    : isRowCalc
                      ? "Arithmetic over other rows already in this table — R<n> = that row's frequency, P<n> = that row's percent"
                      : "Boolean logic — respondents matching this expression are counted";
                  return (
                  <tr key={idx} className="cr-row">
                    <td className="cr-td">
                      <input
                        type="number"
                        className="cr-input cr-input-index"
                        value={row.index}
                        onChange={e => updateCustomRow(idx, 'index', parseInt(e.target.value) || 0)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-label"
                        value={row.label}
                        placeholder="Row label..."
                        onChange={e => updateCustomRow(idx, 'label', e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-logic"
                        value={row.logic}
                        placeholder={logicPlaceholder}
                        title={logicTitle}
                        onChange={e => updateCustomRow(idx, 'logic', e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-logic"
                        value={row.logicBase || ""}
                        placeholder="Base filter (optional)"
                        title="Respondents matching this expression become the % denominator"
                        disabled={isRowCalc || isFormula}
                        onChange={e => updateCustomRow(idx, 'logicBase', e.target.value)}
                      />
                    </td>
                    <td className="cr-td" style={{textAlign:'center'}}>
                      <input
                        type="checkbox"
                        title="Exclude from ranking (keep at bottom)"
                        checked={!!row.norank}
                        onChange={e => updateCustomRow(idx, 'norank', e.target.checked)}
                      />
                    </td>
                    <td className="cr-td" style={{textAlign:'center'}}>
                      <input
                        type="checkbox"
                        title="Keep this row at its original position during ranking"
                        checked={!!row.fix}
                        onChange={e => updateCustomRow(idx, 'fix', e.target.checked)}
                      />
                    </td>
                    <td className="cr-td" style={{textAlign:'center'}}>
                      <input
                        type="checkbox"
                        title="Logic is a Row Calc expression (R1+R2, R1*R4...)"
                        checked={isRowCalc}
                        onChange={e => updateRowMode(idx, 'rowCalc', e.target.checked)}
                      />
                    </td>
                    <td className="cr-td" style={{textAlign:'center'}}>
                      <input
                        type="checkbox"
                        title="Logic is an aggregate formula (Mean(...), Sum(...), Median(...))"
                        checked={isFormula}
                        onChange={e => updateRowMode(idx, 'formula', e.target.checked)}
                      />
                    </td>
                    <td className="cr-td">
                      <button className="cr-del-btn" title="Remove" onClick={() => deleteCustomRow(idx)}>✕</button>
                    </td>
                  </tr>
                  );
                })}
                {customRowsList.length === 0 && (
                  <tr>
                    <td colSpan="9" className="cr-empty">
                      No custom rows. Click "+ Add Row" or paste from Excel above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="no-data-message" style={{ marginTop: 8 }}>
              <strong>Row Calc</strong>: Logic becomes an arithmetic expression over other rows already in this
              table — <code>R&lt;n&gt;</code> for that row's frequency, <code>P&lt;n&gt;</code> for its percent
              (e.g. R1+R2, R1*R4). <strong>Formula</strong>: Logic becomes an aggregate expression over raw survey
              variables — <code>Mean(Q4*3)</code>, <code>Sum(Q4*3, Q5*4, Q2/2)</code>, <code>Median(Q1)</code> — the
              variables can come from any question type (SingleResponse, grid, etc.); multiple comma-separated terms
              are summed per respondent first, then the outer Mean/Sum/Median reduces that to one weighted and one
              unweighted value for the column. Missing values count as 0. Leave both unchecked for the original
              logic-based (count of matching respondents) custom row.
            </div>
          </div>
        );
      }

      case "varNames": {
        const varNameList = selectedVariable?.varName || [];
        const setVarNameList = (updated) => setSelectedVariable((prev) => ({ ...prev, varName: updated }));

        const addVarNameRow = () =>
          setVarNameList([...varNameList, { Name: "", row: "", col: "", stat: "", Location: "" }]);

        const updateVarNameRow = (i, field, value) =>
          setVarNameList(varNameList.map((r, j) => (j === i ? { ...r, [field]: value } : r)));

        const deleteVarNameRow = (i) => setVarNameList(varNameList.filter((_, j) => j !== i));

        const handleVarNamePaste = (e) => {
          const text = e.target.value;
          if (!text.trim()) return;
          const lines = text.trim().split("\n").filter((l) => l.trim());
          const parsed = [];
          lines.forEach((line) => {
            const parts = line.split("\t");
            const [name = "", row = "", col = "", stat = "", location = ""] = parts.map((p) => p.trim());
            if (name) parsed.push({ Name: name, row, col, stat, Location: location });
          });
          if (parsed.length > 0) {
            setVarNameList([...varNameList, ...parsed]);
            e.target.value = "";
          }
        };

        return (
          <div className="cr-container">
            <div className="cr-header">
              <span className="cr-count-badge">
                {varNameList.length} VarName{varNameList.length !== 1 ? "s" : ""}
              </span>
              <button className="cr-add-btn" onClick={addVarNameRow}>+ Add VarName</button>
            </div>

            <div className="cr-paste-area">
              <label className="cr-paste-label">
                Paste from Excel (tab-separated: name | row | col | stat | location — one row per line)
              </label>
              <textarea
                className="cr-paste-textarea"
                placeholder={"Grid_Q1_1\t1\t\t\t10:11\nGrid_Q1_2\t2\t\t\t12:13"}
                onChange={handleVarNamePaste}
              />
            </div>

            <table className="cr-table">
              <thead>
                <tr>
                  <th className="cr-th cr-th-label">Name</th>
                  <th className="cr-th" style={{ width: "12%" }}>Row</th>
                  <th className="cr-th" style={{ width: "12%" }}>Col</th>
                  <th className="cr-th" style={{ width: "12%" }}>Stat</th>
                  <th className="cr-th cr-th-logic">Location</th>
                  <th className="cr-th cr-th-del"></th>
                </tr>
              </thead>
              <tbody>
                {varNameList.map((row, idx) => (
                  <tr key={idx} className="cr-row">
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-label"
                        value={row.Name ?? ""}
                        placeholder="Q1_1"
                        onChange={(e) => updateVarNameRow(idx, "Name", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-index"
                        value={row.row ?? ""}
                        placeholder="Row idx"
                        onChange={(e) => updateVarNameRow(idx, "row", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-index"
                        value={row.col ?? ""}
                        placeholder="Col idx"
                        onChange={(e) => updateVarNameRow(idx, "col", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-index"
                        value={row.stat ?? ""}
                        placeholder="Stat"
                        onChange={(e) => updateVarNameRow(idx, "stat", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-logic"
                        value={row.Location ?? ""}
                        placeholder="10:11"
                        onChange={(e) => updateVarNameRow(idx, "Location", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <button className="cr-del-btn" title="Remove" onClick={() => deleteVarNameRow(idx)}>✕</button>
                    </td>
                  </tr>
                ))}
                {varNameList.length === 0 && (
                  <tr>
                    <td colSpan="6" className="cr-empty">
                      No varNames yet. Click "+ Add VarName" or paste from Excel above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      }

      case "nets": {
        const parseIndices = (str) => {
          if (!str || typeof str !== "string") return [];
          const result = [];
          str.split(",").map(p => p.trim()).forEach(part => {
            if (part.includes("-")) {
              const [s, e] = part.split("-").map(p => parseInt(p.trim(), 10));
              if (!isNaN(s) && !isNaN(e)) for (let i = s; i <= e; i++) result.push(i);
            } else {
              const n = parseInt(part, 10);
              if (!isNaN(n)) result.push(n);
            }
          });
          return [...new Set(result)].sort((a, b) => a - b);
        };

        const showWithin = getPropertyValue("showNetswithinLabels", false);

        return selectedVariable?.Nets?.length > 0 ? (
          <div className="nets-container">
            <div className="nets-header-row">
              <span className="nets-count-badge">{selectedVariable.Nets.length} Net{selectedVariable.Nets.length !== 1 ? "s" : ""}</span>
              <label className="nets-toggle-wrap" title="Show Nets within Labels">
                <span className="nets-toggle-label">Show within labels</span>
                <div
                  className={`prop-toggle-track ${showWithin ? "prop-toggle-track--on" : ""}`}
                  onClick={() => setPropertyValue("showNetswithinLabels", !showWithin)}
                >
                  <div className="prop-toggle-thumb" />
                </div>
              </label>
            </div>
            <div className="nets-list">
              {selectedVariable.Nets.map((net, netIdx) => {
                const isNps = String(net.label ?? '').toLowerCase() === "nps";
                const hasNoIndices = net.indices === null || net.indices === undefined;
                // Comparison-only expression stored in indicesRaw (e.g. ">100", "<50, >200")
                const isCmpExpr = hasNoIndices && !isNps && net.indicesRaw && /[<>]/.test(net.indicesRaw);
                const _safeIndices = Array.isArray(net.indices) ? net.indices : [];
                const displayIndices = (!isCmpExpr && (isNps || hasNoIndices)) ? [...Array(11).keys()] : (_safeIndices.length ? _safeIndices : []);

                // indicesRaw always wins for the input display — it preserves partial
                // input like "55-" while the user is still typing.
                const indicesText = net.indicesRaw !== undefined
                  ? net.indicesRaw
                  : (isNps || hasNoIndices ? "" : _safeIndices.join(", "));

                return (
                  <div key={netIdx} className="net-card">
                    <div className="net-card-header">
                      <div className="net-card-index">#{netIdx + 1}</div>
                      <div className="net-card-realindex-wrap" title="Net index — unique identifier for this net. New nets auto-assign starting at -5555 and count down; editable.">
                        <label className="net-card-realindex-label">Index</label>
                        <input
                          className="net-card-realindex-input"
                          type="text"
                          value={net.index ?? ''}
                          placeholder="—"
                          onChange={(e) => {
                            const raw = e.target.value;
                            const num = Number(raw);
                            const newIndex = raw === '' ? raw : (isNaN(num) ? raw : num);
                            const updated = selectedVariable.Nets.map((n, i) =>
                              i === netIdx ? { ...n, index: newIndex } : n
                            );
                            setSelectedVariable(prev => ({ ...prev, Nets: updated }));
                          }}
                        />
                      </div>
                      <div className="net-card-label-wrap">
                        <input
                          className="net-card-label-input"
                          value={net.label}
                          placeholder="Net label..."
                          onChange={(e) => {
                            const updated = selectedVariable.Nets.map((n, i) =>
                              i === netIdx ? { ...n, label: e.target.value } : n
                            );
                            setSelectedVariable(prev => ({ ...prev, Nets: updated }));
                          }}
                        />
                      </div>
                      <button
                        className="net-card-delete"
                        title="Remove this net"
                        onClick={() => {
                          const updated = selectedVariable.Nets.filter((_, i) => i !== netIdx);
                          setSelectedVariable(prev => ({ ...prev, Nets: updated }));
                          setNets(updated);
                        }}
                      >✕</button>
                    </div>
                    <div className="net-card-indices-edit">
                      <label className="net-indices-label">Indices</label>
                      <input
                        className="net-indices-input"
                        value={indicesText}
                        placeholder="e.g. 1, 2, 55-99"
                        onChange={(e) => {
                          const raw = e.target.value;
                          const parsed = parseIndices(raw);
                          // Pure comparison expression like ">100" or "<50, >200" —
                          // no numeric indices to expand here; backend resolves via indicesRaw.
                          const isCmp = parsed.length === 0 && raw.trim() !== "" && /[<>]/.test(raw);
                          const updated = selectedVariable.Nets.map((n, i) =>
                            i === netIdx
                              ? {
                                  ...n,
                                  indicesRaw: raw,
                                  indices: raw.trim() === "" ? null
                                         : isCmp           ? null   // backend uses indicesRaw
                                         : parsed.length > 0 ? parsed
                                         : n.indices,
                                }
                              : n
                          );
                          setSelectedVariable(prev => ({ ...prev, Nets: updated }));
                        }}
                      />
                    </div>
                    <div className="net-card-indices">
                      {isCmpExpr ? (
                        <span className="net-index-chip net-index-chip--cmp" title="Resolved at calculation time against actual data">
                          <span className="net-index-num">{net.indicesRaw}</span>
                        </span>
                      ) : displayIndices.length > 20 ? (
                        <span className="net-index-chip net-index-chip--range">
                          <span className="net-index-num">{displayIndices[0]}–{displayIndices[displayIndices.length - 1]}</span>
                          <span className="net-indices-count">({displayIndices.length} values)</span>
                        </span>
                      ) : (
                        displayIndices.map((idx, iIdx) => (
                          <span key={iIdx} className="net-index-chip">
                            <span className="net-index-num">{idx}</span>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="no-data-message">No nets defined for this variable</div>
        );
      }

      case "specialRows": {
        // Stacked / Volume / Share row editor — same row shape for all three:
        // { index, label, varNames: [], divisionLogic? (Share only) }
        const cfg = SPECIAL_ROWS[selectedVariable?.type]
          || Object.values(SPECIAL_ROWS).find((c) => (selectedVariable?.[c.prop] || []).length);
        if (!cfg) return <div className="no-data-message">No special rows for this type</div>;

        const list = selectedVariable[cfg.prop] || [];
        const setList = (updated) => setSelectedVariable((prev) => ({ ...prev, [cfg.prop]: updated }));

        const addRow = () =>
          setList([...list, { index: list.length + 1, label: "", varNames: [], ...(cfg.division ? { divisionLogic: "" } : {}) }]);

        const updateRow = (i, field, value) =>
          setList(list.map((r, j) => (j === i ? { ...r, [field]: value } : r)));

        const deleteRow = (i) => setList(list.filter((_, j) => j !== i));

        const parseVars = (text) =>
          String(text || "").split(/[,;]+/).map((s) => s.trim()).filter(Boolean);

        const handleSpecialPaste = (e) => {
          const text = e.target.value;
          if (!text.trim()) return;
          const lines = text.trim().split("\n").filter((l) => l.trim());
          const parsed = [];
          lines.forEach((line, i) => {
            const parts = line.split("\t");
            let idx, label, vars, division;
            if (parts.length === 1) {
              idx = list.length + parsed.length + 1; label = parts[0].trim(); vars = []; division = "";
            } else if (parts.length === 2) {
              idx = list.length + parsed.length + 1; label = parts[0].trim(); vars = parseVars(parts[1]); division = "";
            } else {
              idx = parseInt(parts[0].trim()) || (list.length + parsed.length + 1);
              label = parts[1].trim();
              vars = parseVars(parts[2]);
              division = (parts[3] || "").trim();
            }
            if (label) {
              const row = { index: idx, label, varNames: vars };
              if (cfg.division) row.divisionLogic = division;
              parsed.push(row);
            }
          });
          if (parsed.length > 0) {
            setList([...list, ...parsed]);
            e.target.value = "";
          }
        };

        return (
          <div className="cr-container">
            <div className="cr-header">
              <span className="cr-count-badge">
                {list.length} {cfg.label.replace(" Rows", "")} Row{list.length !== 1 ? "s" : ""}
              </span>
              <button className="cr-add-btn" onClick={addRow}>+ Add Row</button>
            </div>

            {cfg.division && (
              <div className="prop-text-field prop-text-field--wide" style={{ marginBottom: 10 }}>
                <label className="prop-text-label">
                  Default Division (used when a row has no division of its own — logic counts respondents, variable names are summed)
                </label>
                <input
                  type="text"
                  className="prop-text-input"
                  value={getPropertyValue("defaultDivision", "")}
                  placeholder="e.g. S1=1  or  Q7r1, Q7r2"
                  onChange={(e) => handlePropertyChange(e, "defaultDivision")}
                />
              </div>
            )}

            <div className="cr-paste-area">
              <label className="cr-paste-label">
                Paste from Excel (tab-separated: index | label | variables{cfg.division ? " | division" : ""} — one row per line)
              </label>
              <textarea
                className="cr-paste-textarea"
                placeholder={`1\tBrand A\tQ7r1, Q7r2${cfg.division ? "\tS1=1" : ""}\n2\tBrand B\tQ8r1${cfg.division ? "\t" : ""}`}
                onChange={handleSpecialPaste}
              />
            </div>

            <table className="cr-table">
              <thead>
                <tr>
                  <th className="cr-th cr-th-index">Index</th>
                  <th className="cr-th cr-th-label">Label</th>
                  <th className="cr-th cr-th-logic">Variables (comma-separated)</th>
                  {cfg.division && <th className="cr-th cr-th-logic">Division</th>}
                  <th className="cr-th cr-th-del"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((row, idx) => (
                  <tr key={idx} className="cr-row">
                    <td className="cr-td">
                      <input
                        type="number"
                        className="cr-input cr-input-index"
                        value={row.index}
                        onChange={(e) => updateRow(idx, "index", parseInt(e.target.value) || 0)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-label"
                        value={row.label}
                        placeholder="Row label..."
                        onChange={(e) => updateRow(idx, "label", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-logic"
                        value={row.varNamesText !== undefined ? row.varNamesText : (row.varNames || []).join(", ")}
                        placeholder="Q7r1, Q7r2"
                        onChange={(e) =>
                          setList(list.map((r, j) =>
                            j === idx
                              ? { ...r, varNamesText: e.target.value, varNames: parseVars(e.target.value) }
                              : r
                          ))
                        }
                      />
                    </td>
                    {cfg.division && (
                      <td className="cr-td">
                        <input
                          type="text"
                          className="cr-input cr-input-logic"
                          value={row.divisionLogic || ""}
                          placeholder="Logic (count) or variables (sum)"
                          title="A logic expression counts matching respondents; variable names are summed. Empty = use Default Division."
                          onChange={(e) => updateRow(idx, "divisionLogic", e.target.value)}
                        />
                      </td>
                    )}
                    <td className="cr-td">
                      <button className="cr-del-btn" title="Remove" onClick={() => deleteRow(idx)}>✕</button>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td colSpan={cfg.division ? 5 : 4} className="cr-empty">
                      No rows yet. Click "+ Add Row" or paste from Excel above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      }

      case "rowCalcs": {
        const list = selectedVariable?.RowCalcs || [];
        const setList = (updated) => setSelectedVariable((prev) => ({ ...prev, RowCalcs: updated }));

        const addRow = () => setList([...list, { label: "", expr: "" }]);

        const updateRow = (i, field, value) =>
          setList(list.map((r, j) => (j === i ? { ...r, [field]: value } : r)));

        const deleteRow = (i) => setList(list.filter((_, j) => j !== i));

        return (
          <div className="cr-container">
            <div className="cr-header">
              <span className="cr-count-badge">
                {list.length} Row Calc{list.length !== 1 ? "s" : ""}
              </span>
              <button className="cr-add-btn" onClick={addRow}>+ Add Row Calc</button>
            </div>

            <table className="cr-table">
              <thead>
                <tr>
                  <th className="cr-th cr-th-label">Label</th>
                  <th className="cr-th cr-th-logic">Calculation</th>
                  <th className="cr-th cr-th-del"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((row, idx) => (
                  <tr key={idx} className="cr-row">
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-label"
                        value={row.label}
                        placeholder="Row label..."
                        onChange={(e) => updateRow(idx, "label", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-logic"
                        value={row.expr}
                        placeholder="e.g. R1+R2+R3, R1*R4, R1/R2, P1+P4, R-5556"
                        onChange={(e) => updateRow(idx, "expr", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <button className="cr-del-btn" title="Remove" onClick={() => deleteRow(idx)}>✕</button>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td colSpan="3" className="cr-empty">
                      No row calculations yet. Click "+ Add Row Calc".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="no-data-message" style={{ marginTop: 8 }}>
              Reference any row by its index number: <code>R&lt;n&gt;</code> pulls that row's frequency (e.g. R1, R4, or
              R-5556 for a Net row), <code>P&lt;n&gt;</code> pulls that row's own percentage instead. Combine with
              + − × ÷ and parentheses — e.g. R1+R2+R3, R1*R4, R1/R2, P1+P4, (R1+R2)/R3. Mixing R and P in one
              expression is allowed but the result is treated as a percentage (no raw count) as soon as any P appears.
            </div>
          </div>
        );
      }

      case "combine": {
        const toggleCombineKey = (key) => {
          setCombineSelectedKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
          );
        };
        return (
          <div className="cr-container">
            <div className="cr-header">
              <span className="cr-count-badge">{combineSelectedKeys.length} Selected</span>
            </div>
            <div className="prop-text-field prop-text-field--wide" style={{ marginBottom: 12 }}>
              <label className="prop-text-label">New Question Title (optional)</label>
              <input
                type="text"
                className="prop-text-input"
                value={combineTitle}
                placeholder="Defaults to '<first variable> (Combined)'"
                onChange={(e) => setCombineTitle(e.target.value)}
              />
            </div>
            <table className="cr-table">
              <thead>
                <tr>
                  <th className="cr-th" style={{ width: "7%" }}>Pick</th>
                  <th className="cr-th" style={{ width: "14%" }}>Key</th>
                  <th className="cr-th cr-th-label">Question</th>
                  <th className="cr-th" style={{ width: "18%" }}>Type</th>
                </tr>
              </thead>
              <tbody>
                {combineCandidates.map(([key, v]) => (
                  <tr key={key} className="cr-row">
                    <td className="cr-td" style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={combineSelectedKeys.includes(key)}
                        onChange={() => toggleCombineKey(key)}
                      />
                    </td>
                    <td className="cr-td" style={{ fontFamily: "monospace", fontWeight: 600 }}>{key}</td>
                    <td className="cr-td">{v.Qtitle || key}</td>
                    <td className="cr-td">{v.type}</td>
                  </tr>
                ))}
                {combineCandidates.length === 0 && (
                  <tr>
                    <td colSpan="4" className="cr-empty">
                      No SingleResponse or Multiple2DGrid questions available to combine.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div style={{ marginTop: 12 }}>
              <button
                className="cr-add-btn"
                disabled={combineSelectedKeys.length === 0 || loading}
                onClick={handleCreateCombinedVariable}
              >
                + Create Combined Variable
              </button>
            </div>
          </div>
        );
      }

      default:
        return <div>Select a tab to view content</div>;
    }
  };

  const handleRefreshFromSpss = async () => {
    if (!selectedVariable || !selectedKey) return;

    const varNames = selectedVariable.varName;
    if (!varNames || varNames.length === 0) {
      toast.warning("No varNames defined for this variable — cannot refresh from SPSS.");
      return;
    }

    const confirmed = await confirm({
      title: "Refresh Data from SPSS",
      message: `This will reload data for "${selectedKey}" (${varNames.length} variable(s)) from the SPSS file and overwrite the current values in the database. This cannot be undone. Proceed?`,
      confirmText: "Refresh",
    });
    if (!confirmed) return;

    setRefreshLoading(true);
    try {
      const { data } = await apiService.post('/refresh-variable-from-spss', {
        projectId,
        varNames,
        user: powner || '',
      });

      if (!data.success) {
        toast.error("Refresh failed: " + (data.error || "Unknown error"));
        return;
      }

      toast.success(data.message || `Refreshed ${data.updated?.length || 0} variable(s) from SPSS`);
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.errors.length} variable(s) had issues: ${data.errors.join("; ")}`);
      }
    } catch (error) {
      toast.error("Error refreshing from SPSS: " + error.message);
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleSwitchBetween = (from, to) => {
    const fromItems = selectedVariable[from] || [];
    const toItems = selectedVariable[to] || [];
    const updatedVariable = { ...selectedVariable, [from]: toItems, [to]: fromItems };
    setSelectedVariable(updatedVariable);
    setdataGroup((prev) => ({ ...prev, [selectedKey]: updatedVariable }));
  };

  const TAB_SCRIPT_PREVIEW_CONFIG = {
    wincross: { endpoint: "/generate-preview-wincross", title: "Wincross Table Script", feature: "wincross" },
    uncle: { endpoint: "/generate-preview-uncle", title: "Uncle Table Script", feature: "uncle" },
    quantum: { endpoint: "/generate-preview-quantum", title: "Quantum Table Script", feature: "quantum" },
  };

  // Read-only preview of the tab-export script for just the selected
  // question. Reuses the exact same wgenerate_files/generate_files/
  // qgenerate_files functions the real Wincross/Uncle/Quantum exports use
  // (via a preview=True, render_keys=[selectedKey] mode on the backend),
  // so it can never drift from what actually gets exported — no file
  // write, no OneDrive upload. Quantum returns multiple pieces (its own
  // per-question .qtm file for grid types, plus the shared axes/banner
  // code) since it splits question code across separate files.
  const previewTabScript = async (kind) => {
    if (!selectedVariable || !selectedKey) {
      toast.warning("Please select a question first");
      return;
    }
    const cfg = TAB_SCRIPT_PREVIEW_CONFIG[kind];
    setScriptPreviewTitle(cfg.title);
    setScriptPreviewData(null);
    setScriptPreviewLoading(true);
    setScriptPreviewOpen(true);
    try {
      const { data } = await apiService.post(cfg.endpoint, {
        dataList: dataGroup,
        selectedKey,
        banners,
        project: JSON.stringify(selectedProject),
        client: JSON.stringify(selectedClient),
      });
      setScriptPreviewData(data);
    } catch (error) {
      toast.error(error.message || "Failed to generate preview");
      setScriptPreviewOpen(false);
    } finally {
      setScriptPreviewLoading(false);
    }
  };

  const copyScriptPreviewToClipboard = () => {
    let text = "";
    if (typeof scriptPreviewData === "string") {
      text = scriptPreviewData;
    } else if (scriptPreviewData) {
      const parts = (scriptPreviewData.questionFiles || []).map(
        (f) => `--- ${f.name} ---\n${f.content}`
      );
      parts.push(`--- Axes ---\n${scriptPreviewData.axes || ""}`);
      parts.push(`--- Banner ---\n${scriptPreviewData.banner || ""}`);
      text = parts.join("\n\n");
    }
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("Code copied to clipboard!"))
      .catch(() => toast.error("Failed to copy code"));
  };

  const hasRows = selectedVariable?.Rows?.length > 0;
  const specialCfg = selectedVariable
    ? (SPECIAL_ROWS[selectedVariable.type]
       || Object.values(SPECIAL_ROWS).find((c) => (selectedVariable[c.prop] || []).length > 0))
    : null;
  const hasCols = selectedVariable?.Cols?.length > 0;
  const hasStatements = selectedVariable?.Statements?.length > 0;
  const hasValues = selectedVariable?.values?.length > 0;
  const hasValueLabels = selectedVariable?.valueLabels && Object.keys(selectedVariable.valueLabels).length > 0;
  const hasNets = selectedVariable?.Nets?.length > 0;

  return (
    <div className="variable-properties">
      <h2>{selectedKey || "No variable selected"}</h2>

      <div className="tab-navigation">
        <CommonButton
          title="Properties Tab"
          label="Properties"
          onClick={() => setActiveTab("properties")}
          variant="tab"
          isSelected={activeTab === "properties"}
          size="small"
          bold={true}
        />

        <CommonButton
          title="Checkbox Properties Tab — Settings + Ranking toggles"
          label="Checkboxes"
          onClick={() => setActiveTab("checkboxes")}
          variant="tab"
          isSelected={activeTab === "checkboxes"}
          size="small"
          bold={true}
        />

        <CommonButton
          title="Fields Tab — text inputs and dropdowns (Type, Titles, Logic, Min/Max, etc.)"
          label="Fields"
          onClick={() => setActiveTab("fields")}
          variant="tab"
          isSelected={activeTab === "fields"}
          size="small"
          bold={true}
        />

        {hasRows && (
          <CommonButton
            title={`Rows Tab (${selectedVariable.Rows.length})`}
            label={`Rows (${selectedVariable.Rows.length})`}
            onClick={() => setActiveTab("rows")}
            variant="tab"
            isSelected={activeTab === "rows"}
            size="small"
            bold={true}
          />
        )}

        {hasCols && (
          <>
            <CommonButton
              label="↔"
              title="Switch Cols ↔ Rows"
              onClick={() => handleSwitchBetween("Cols", "Rows")}
              variant="arrow"
            />
            <CommonButton
              title={`Columns Tab (${selectedVariable.Cols.length})`}
              label={`Columns (${selectedVariable.Cols.length})`}
              onClick={() => setActiveTab("cols")}
              variant="tab"
              isSelected={activeTab === "cols"}
              size="small"
              bold={true}
            />
          </>
        )}

        {hasStatements && (
          <>
            <CommonButton
              label="↔"
              title="Switch Cols ↔ Statements"
              onClick={() => handleSwitchBetween("Cols", "Statements")}
              variant="arrow"
            />
            <CommonButton
              title={`Statements Tab (${selectedVariable.Statements.length})`}
              label={`Statements (${selectedVariable.Statements.length})`}
              onClick={() => setActiveTab("statements")}
              variant="tab"
              isSelected={activeTab === "statements"}
              size="small"
              bold={true}
            />
            <CommonButton
              label="⤴"
              title="Switch Rows ↔ Statements"
              onClick={() => handleSwitchBetween("Rows", "Statements")}
              variant="arrow"
            />
          </>
        )}

        {hasValues && (
          <CommonButton
            title={`Values Tab (${selectedVariable.values.length})`}
            label={`Values (${selectedVariable.values.length})`}
            onClick={() => setActiveTab("values")}
            variant="tab"
            isSelected={activeTab === "values"}
            size="small"
            bold={true}
          />
        )}

        {hasValueLabels && (
          <CommonButton
            title={`Value Labels Tab (${Object.keys(selectedVariable.valueLabels).length})`}
            label={`Value Labels (${Object.keys(selectedVariable.valueLabels).length})`}
            onClick={() => setActiveTab("valueLabels")}
            variant="tab"
            isSelected={activeTab === "valueLabels"}
            size="small"
            bold={true}
          />
        )}

        {hasNets && (
          <CommonButton
            title={`Nets Tab (${selectedVariable.Nets.length})`}
            label={`Nets (${selectedVariable.Nets.length})`}
            onClick={() => setActiveTab("nets")}
            variant="tab"
            isSelected={activeTab === "nets"}
            size="small"
            bold={true}
          />
        )}

        <CommonButton
          title="Custom Rows Tab"
          label={selectedVariable?.customRows?.length ? `Custom Rows (${selectedVariable.customRows.length})` : "Custom Rows"}
          onClick={() => setActiveTab("customRows")}
          variant="tab"
          isSelected={activeTab === "customRows"}
          size="small"
          bold={true}
        />

        <CommonButton
          title="Combine Variables — merge SingleResponse/Multiple2DGrid questions into one new Custom question"
          label={combineSelectedKeys.length ? `Combine Variables (${combineSelectedKeys.length})` : "Combine Variables"}
          onClick={() => setActiveTab("combine")}
          variant="tab"
          isSelected={activeTab === "combine"}
          size="small"
          bold={true}
        />

        <CommonButton
          title="VarNames Tab — the underlying SPSS variable(s) mapped to this question, each with optional row/col/stat"
          label={selectedVariable?.varName?.length ? `VarNames (${selectedVariable.varName.length})` : "VarNames"}
          onClick={() => setActiveTab("varNames")}
          variant="tab"
          isSelected={activeTab === "varNames"}
          size="small"
          bold={true}
        />

        {specialCfg && (
          <CommonButton
            title={`${specialCfg.label} Tab`}
            label={`${specialCfg.label} (${(selectedVariable[specialCfg.prop] || []).length})`}
            onClick={() => setActiveTab("specialRows")}
            variant="tab"
            isSelected={activeTab === "specialRows"}
            size="small"
            bold={true}
          />
        )}

        { (
          <CommonButton
            title="Row Calculations Tab"
            label={selectedVariable?.RowCalcs?.length ? `Row Calcs (${selectedVariable.RowCalcs.length})` : "Row Calcs"}
            onClick={() => setActiveTab("rowCalcs")}
            variant="tab"
            isSelected={activeTab === "rowCalcs"}
            size="small"
            bold={true}
          />
        )}

        <CommonButton
          title="Chart Tab — Add to Chart configuration"
          label="Chart"
          onClick={() => setActiveTab("chart")}
          variant="tab"
          isSelected={activeTab === "chart"}
          size="small"
          bold={true}
        />

        <CommonButton
          title="View and edit the complete JSON for this question"
          label="View JSON"
          onClick={() => setIsJsonModalOpen(true)}
          variant="tab"
          isSelected={isJsonModalOpen}
          size="small"
          bold={true}
        />

        {selectedVariable && (!allowedExports || allowedExports.includes("wincross")) && (
          <CommonButton
            title="Preview this question's extracted Wincross table script (read-only)"
            label="Wincross"
            onClick={() => previewTabScript("wincross")}
            variant="tab"
            size="small"
            bold={true}
          />
        )}

        {selectedVariable && (!allowedExports || allowedExports.includes("uncle")) && (
          <CommonButton
            title="Preview this question's extracted Uncle table script (read-only)"
            label="Uncle"
            onClick={() => previewTabScript("uncle")}
            variant="tab"
            size="small"
            bold={true}
          />
        )}

        {selectedVariable && (!allowedExports || allowedExports.includes("quantum")) && (
          <CommonButton
            title="Preview this question's extracted Quantum table script (read-only) — includes its axes/banner code"
            label="Quantum"
            onClick={() => previewTabScript("quantum")}
            variant="tab"
            size="small"
            bold={true}
          />
        )}
      </div>

      {selectedVariable?.tb && (
        <div className="tabulation-syntax-display">
          <h3>Generated Syntax</h3>
          <pre>{selectedVariable.tb}</pre>
        </div>
      )}

      <div className="tab-content">{renderTabContent()}</div>

      {selectedVariable && (
        <div className="action-buttons">
          <CommonButton
            title="Save Changes"
            label={loading ? "Saving..." : "Save Changes"}
            onClick={saveChanges}
            variant="primary"
            size="large"
            loading={loading}
            bold={true}
          />

          {selectedVariable?.varName?.length > 0 && (
            <CommonButton
              title="Re-fetch this variable's data from the SPSS file and update the database"
              label={refreshLoading ? "Refreshing..." : "Refresh from SPSS"}
              onClick={handleRefreshFromSpss}
              variant="secondary"
              size="large"
              loading={refreshLoading}
              bold={true}
            />
          )}

          <CommonButton
            title="Change this question's type — split grids, factor 2D→3D, switch Single/Numeric/Multiple, or convert to Custom/Stacked/Volume/Share"
            label="Change Type"
            onClick={() => setConvertOpen(true)}
            variant="secondary"
            size="large"
            bold={true}
          />

          <CommonButton
            title="Duplicate this question (the copy can then be converted to another type)"
            label="Duplicate"
            onClick={handleDuplicate}
            variant="secondary"
            size="large"
            bold={true}
          />

          {convSnapshot && (
            <CommonButton
              title="Revert the last type conversion / duplicate"
              label="Undo Conversion"
              onClick={undoConversion}
              variant="secondary"
              size="large"
              bold={true}
            />
          )}


          {selectedVariable.type &&
            (selectedVariable.type.includes("2DGrid") ||
             selectedVariable.type.includes("3DGrid") ||
             selectedVariable.type.includes("Grid")) && (
            <CommonButton
              title="Swap Rows and Columns"
              label="Swap Rows/Cols"
              onClick={handleSwapRowsAndCols}
              variant="secondary"
              size="large"
              bold={true}
            />
          )}

          {selectedVariable.type === 'Single2DGrid' && (
            <CommonButton
              title="Convert to Custom — one custom row per value, with logic OR-ing that value across every grid varName (e.g. Var1=1 or Var2=1 or Var3=1). Values will be removed."
              label="OE Combo"
              onClick={handleOECombo}
              variant="secondary"
              size="large"
              bold={true}
            />
          )}

          {(selectedVariable?.type === 'Custom' || (selectedVariable?.customRows?.length > 0)) && (
            <CommonButton
              title={
                selectedVariable?.type === 'Custom'
                  ? "Convert Custom rows to Multiple2DGrid — creates binary Yes/No SPSS variables for each custom row with logic"
                  : selectedVariable?.type === 'Multiple2DGrid'
                    ? "Merge custom rows into this Multiple2DGrid — appends new rows and SPSS variables to the existing ones"
                    : selectedVariable?.type === 'Single2DGrid'
                      ? "Write each custom row's code into the data of every grid variable where its condition is met, and append the value label — no new variables are created"
                      : `Create a new "${selectedKey}new" Multiple2DGrid variable from custom rows, removing custom rows from the original`
              }
              label={loading ? "Saving..." : "Save Variable"}
              onClick={handleSaveCustomVariable}
              variant="primary"
              size="large"
              loading={loading}
              bold={true}
            />
          )}

          {(selectedVariable?.type === 'Multiple2DGrid' ||
            selectedVariable?.type === 'Multiple3DGrid' ||
            selectedVariable?.type === 'Multiple4DGrid') && (
            <CommonButton
              title={`Create a new Numeric variable "${selectedKey}sm" summing every non-exclusive row variable in this question, plus a COMPUTE/SUM SPSS syntax snippet`}
              label={loading ? "Saving..." : "Average Mention"}
              onClick={handleAverageMention}
              variant="secondary"
              size="large"
              loading={loading}
              bold={true}
            />
          )}

          {loading && <Loader loading={loading} />}
        </div>
      )}

      <ConvertTypeModal
        isOpen={convertOpen}
        onClose={() => setConvertOpen(false)}
        selectedKey={selectedKey}
        variable={selectedVariable}
        dataGroup={dataGroup}
        onApply={applyConversionPatch}
      />

      <QuestionJsonModal
        isOpen={isJsonModalOpen}
        onClose={() => setIsJsonModalOpen(false)}
        selectedVariable={selectedVariable}
        setSelectedVariable={setSelectedVariable}
      />

      {scriptPreviewOpen && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={() => setScriptPreviewOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "12px",
              width: "80%",
              maxWidth: "900px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
                borderBottom: "2px solid var(--color-border)",
                paddingBottom: "10px",
              }}
            >
              <h3 style={{ margin: 0, color: "var(--color-gray-800, #1f2937)" }}>{scriptPreviewTitle}</h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={copyScriptPreviewToClipboard}
                  disabled={scriptPreviewLoading || !scriptPreviewData}
                  style={{
                    background: "var(--color-success)",
                    color: "var(--color-white)",
                    border: "none",
                    borderRadius: "var(--radius-xl)",
                    padding: "8px 18px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  Copy Code
                </button>
                <button
                  onClick={() => setScriptPreviewOpen(false)}
                  style={{
                    background: "var(--color-gray-100)",
                    color: "var(--color-gray-700)",
                    border: "none",
                    borderRadius: "var(--radius-xl)",
                    padding: "8px 18px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div
              style={{
                flex: 1,
                overflow: "auto",
                background: "var(--color-bg-subtle, #f8fafc)",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "15px",
              }}
            >
              {scriptPreviewLoading ? (
                <div style={{ color: "var(--color-gray-600, #4b5563)" }}>Generating preview…</div>
              ) : typeof scriptPreviewData === "string" ? (
                <pre
                  style={{
                    margin: 0,
                    fontFamily: "monospace",
                    fontSize: "13px",
                    lineHeight: "1.5",
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    color: "var(--color-gray-900, #1f2937)",
                  }}
                >
                  {scriptPreviewData}
                </pre>
              ) : scriptPreviewData ? (
                <>
                  {(scriptPreviewData.questionFiles || []).map((f) => (
                    <React.Fragment key={f.name}>
                      <div style={{ fontWeight: 600, marginBottom: "8px", color: "var(--color-gray-800, #1f2937)" }}>
                        {f.name}
                      </div>
                      <pre
                        style={{
                          margin: "0 0 20px",
                          fontFamily: "monospace",
                          fontSize: "13px",
                          lineHeight: "1.5",
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                          color: "var(--color-gray-900, #1f2937)",
                        }}
                      >
                        {f.content}
                      </pre>
                    </React.Fragment>
                  ))}
                  <div style={{ fontWeight: 600, marginBottom: "8px", color: "var(--color-gray-800, #1f2937)" }}>
                    Axes
                  </div>
                  <pre
                    style={{
                      margin: "0 0 20px",
                      fontFamily: "monospace",
                      fontSize: "13px",
                      lineHeight: "1.5",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      color: "var(--color-gray-900, #1f2937)",
                    }}
                  >
                    {scriptPreviewData.axes}
                  </pre>
                  <div style={{ fontWeight: 600, marginBottom: "8px", color: "var(--color-gray-800, #1f2937)" }}>
                    Banner
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      fontFamily: "monospace",
                      fontSize: "13px",
                      lineHeight: "1.5",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      color: "var(--color-gray-900, #1f2937)",
                    }}
                  >
                    {scriptPreviewData.banner}
                  </pre>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* {JSON.stringify(selectedVariable)} */}
    </div>
  );
};

export default PropertiesComponent;